/**
 * Augmented Live publisher — pure planning logic (Phase 1).
 * Design: docs/design/here-now-s3-realtime.md
 *
 * This is the self-hosted artifact publisher. Where a vendor publisher
 * would orchestrate a
 * three-step presigned dance against a vendor API, Augmented Live owns the
 * origin: the API writes immutable, versioned objects to our S3 bucket and a
 * CloudFront distribution serves them.
 *
 * To keep that I/O testable and Workers-safe, this module is **pure**: it
 * computes *what* to write (object keys + bodies + cache headers), the next
 * version, the public URL, and the realtime broadcast to emit — but performs
 * no S3, DB, or network calls. The API-side orchestrator (the integration
 * broker dispatch) executes the plan.
 *
 * Object layout (immutable versions → no CloudFront invalidations):
 *   {slug}                         the viewer shell — Cache-Control: no-cache
 *   {slug}/content/{version}.html  the artifact body — immutable, 1y
 *
 * The shell is stored at the *bare* slug key (S3's flat keyspace lets the
 * object `{slug}` and the prefix `{slug}/…` coexist), so with a Router
 * `routeBucket('/', bucket)` the canonical URL `https://{cdnDomain}/{slug}`
 * maps 1:1 to the shell object and `…/{slug}/content/{v}.html` maps 1:1 to a
 * content object — no CloudFront URL-rewrite function needed. The shell hosts
 * the artifact body in a sandboxed iframe and subscribes to the public
 * realtime channel `artifact:{slug}`, repointing the iframe at the new
 * immutable content object when a `published` ping with a higher version
 * arrives.
 */

import { MARKUP_BRIDGE_SCRIPT, MARKUP_MARKER, MARKUP_CONTROL_MARKER } from './markup.js';
import { VIEW_ANALYTICS_BEACON, VIEW_REPORTER_SCRIPT } from './view-beacon.js';
import { hasRenderableItems } from './renderer.js';

export const HTML_CONTENT_TYPE = 'text/html; charset=utf-8';

/** Immutable content objects can be cached forever — the version is in the key. */
export const CONTENT_CACHE_CONTROL = 'public, max-age=31536000, immutable';
/** The shell is the mutable pointer; it must revalidate so a re-publish is seen on cold load. */
export const SHELL_CACHE_CONTROL = 'no-cache';

/**
 * ENG-8518 — the generation number of `renderShell`'s output.
 *
 * WHY THIS EXISTS. `no-cache` on the shell object only guarantees the browser
 * re-fetches it; it says nothing about the object being current. The whole
 * viewer script is INLINED into the shell and written to S3 at publish time, so
 * a fix to the bridge, the overlay, the analytics beacon or the chrome reaches
 * new publishes only — every artefact published before the fix keeps serving its
 * publish-time snapshot forever. There is no expiry on that: without a
 * re-render, "we fixed it" and "customers see the fix" are different claims.
 *
 * ENG-6788 shipped a manual `backfill:live-shells` script for exactly this, and
 * that is the tell: a correctness mechanism that depends on someone remembering
 * to run it is not a mechanism. This constant makes the state MACHINE-READABLE
 * instead — `published_artefacts.shell_version` records what each artefact was
 * last rendered with, the reconciler cron re-renders anything behind, and the
 * value is stamped into the page (`<meta name="agt-shell-version">`) so the
 * serving generation is observable from the artefact itself.
 *
 * BUMP THIS whenever renderShell's output changes in a way that should reach
 * already-published artefacts. You do not have to remember: `scripts/
 * check-live-shell-version.mjs` fails CI when the rendered output changes and
 * this number does not. Forgetting the bump is the failure mode this whole
 * ticket is about, so it is a guard, not a convention.
 *
 * 8 (ENG-10632, merged over ENG-10677): TWO branches bumped 6 -> 7 in parallel
 * with DIFFERENT renders - the caret-click focus guard here, and the free-tier
 * attribution on main. Main's 7 was already in production when they met. Keeping
 * 7 for the combined render would have made the reconciler treat every artefact
 * main had re-rendered as current, so the caret fix would never have reached
 * them, and the guard would have passed (recorded hash == render, version
 * unchanged). A merge of two shell changes is a third shell, so it is a new
 * version. Resolving the hash conflict by picking a side, or by re-recording at
 * 7, both ship silently broken.
 */
// CS-1763 hotfix: 9 -> 10, and the bump is MANDATORY here rather than tidy.
// Version 9 is the BROKEN shell - it merged from an enqueued sha that predated the
// collision fix, and live-shell-reconcile has been carrying it to every published deck.
// The reconciler selects on shell_version < target, so leaving this at 9 would mean every
// deck already rewritten at 9 keeps the pill sitting on its prev button forever. 10 is what
// reaches them.
//
// CS-1763 part 2: 8 -> 9. The bump is the POINT, not bookkeeping - the shell is
// `no-cache` and `live-shell-reconcile` rewrites every stale row, so raising
// this is what carries the mobile fix to decks published months ago. The slide
// DOM could not be reached this way (content objects are immutable), which is
// the whole reason this option was chosen over reflowing the slides.
export const SHELL_VERSION = 10;

/**
 * ENG-9516 — the overlay controls that hide in presenter mode.
 *
 * WHAT IS IN, AND WHY THESE FOUR. They are the interactive controls a presenter does
 * not want on screen while a customer is looking at the slide: the Download pill, the
 * chat launcher, View Stats, and the Edit Mode toggle. Brad's words were "when we're
 * in presentations it's annoying having the Edit, Download etc buttons".
 *
 * WHAT IS DELIBERATELY OUT, because "hide the chrome" is not "hide everything
 * overlaid":
 *
 *   #al-attribution — the free-tier "Made with Ninjafy" link is a commercial
 *     mechanism (ADR-0042 / ENG-7469), not presenter chrome. A keystroke that strips
 *     attribution from a free-tier page is a pricing decision, and not one to make
 *     inside a convenience feature.
 *
 *   #al-sunset — tells the viewer the page is about to be DELETED (ENG-7470). Hiding a
 *     data-loss warning to make a slide look tidier trades the user's interest for the
 *     presenter's. If it is noisy during a demo, that is an argument about the banner,
 *     to be had on its own terms.
 */
export const PRESENTER_CHROME_SELECTORS = '#al-download, .al-launcher, .al-stats-btn, .al-edit-btn';

/** Idle delay before the chrome fades on its own. Matches the video-player convention. */
export const CHROME_IDLE_MS = 4000;

/**
 * Two INDEPENDENT states, both of which hide the chrome, deliberately not merged:
 *
 *   [data-al-chrome="hidden"] — explicit. The presenter pressed H. Sticky, persisted
 *     per artefact, survives slide changes and reloads. Only H clears it, because a
 *     presenter who hid the chrome and then moved the mouse did not mean "put it back".
 *
 *   [data-al-idle="1"] — transient. Nobody has touched anything for CHROME_IDLE_MS.
 *     ANY input clears it. This is the one that works without being learned.
 *
 * `visibility:hidden` rather than opacity alone: an invisible control still in the tab
 * order is a focus trap you cannot see.
 */
const presenterChromeCss = `
  /* ENG-9516: presenter mode. */
  ${PRESENTER_CHROME_SELECTORS}{transition:opacity .25s ease,visibility .25s ease}
  html[data-al-chrome="hidden"] :is(${PRESENTER_CHROME_SELECTORS}),
  html[data-al-idle="1"] :is(${PRESENTER_CHROME_SELECTORS}){
    opacity:0;visibility:hidden;pointer-events:none}
  /* A shortcut nobody knows about does not exist (ENG-9516). Shown once per browser,
     on pointer-fine devices only, and dismissed by any use of the key. */
  #al-chrome-hint{position:fixed;left:50%;top:18px;transform:translateX(-50%);
    z-index:2147483646;font-family:system-ui,-apple-system,sans-serif;font-size:12.5px;
    font-weight:600;color:#fff;background:rgba(11,16,32,.86);padding:.4rem .8rem;
    border-radius:999px;box-shadow:0 2px 10px rgba(0,0,0,.25);
    opacity:0;visibility:hidden;transition:opacity .3s ease,visibility .3s ease}
  #al-chrome-hint[data-show="1"]{opacity:1;visibility:visible}
  #al-chrome-hint kbd{font:inherit;background:rgba(255,255,255,.18);
    border-radius:4px;padding:0 .3rem;margin:0 .1rem}
  @media (prefers-reduced-motion:reduce){
    ${PRESENTER_CHROME_SELECTORS},#al-chrome-hint{transition:none}
  }`;

/**
 * ENG-9516 — presenter mode. Two ways to get the overlay controls off the slide: press
 * H (explicit, sticky, remembered per artefact) or simply stop touching anything for a
 * few seconds (transient, restored by any input).
 *
 * The persisted half is per-BROWSER, which is what keeps the ticket's "do not change
 * what a shared viewer sees by default" promise honest: the presenter's preference
 * lives in their own localStorage, and a recipient opening the same link still gets the
 * chrome, and their Download button, exactly as before.
 *
 * Exported (rather than inlined into `renderShell`) so its test drives THIS source
 * instead of scraping it back out of a rendered page — a test that re-derives the thing
 * it is testing will happily agree with a broken copy.
 *
 * Reads `cfg.slug` from the surrounding shell scope.
 */
export function presenterModeScript(): string {
  return `
  (() => {
    const root = document.documentElement;
    const KEY = 'al:chrome:' + cfg.slug;
    const HINT_KEY = 'al:chrome-hint-seen';
    // localStorage throws outright in some privacy modes, so every touch is guarded.
    // A viewer with storage disabled loses only the memory, not the key.
    const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
    const write = (k, v) => { try { localStorage.setItem(k, v); } catch { /* not fatal */ } };

    const setHidden = (hide) => {
      // Blur first: hiding the element under the caret would otherwise leave focus on
      // something nobody can see.
      const ae = document.activeElement;
      if (hide && ae && ae.closest && ae.closest(${JSON.stringify(PRESENTER_CHROME_SELECTORS)})) ae.blur();
      if (hide) root.setAttribute('data-al-chrome', 'hidden');
      else root.removeAttribute('data-al-chrome');
      write(KEY, hide ? '1' : '0');
    };

    // Restore this presenter's preference for THIS artefact. Absent => visible.
    if (read(KEY) === '1') root.setAttribute('data-al-chrome', 'hidden');

    // --- the explicit control ------------------------------------------------
    // H, not Esc (the browser and fullscreen own Esc) and not an arrow/space/page key
    // (the deck pager owns those). Ignored while typing - the comment box is a real
    // textarea and swallowing an "h" there would be a worse bug than the one this
    // fixes - and ignored with a modifier held, so browser shortcuts win.
    const isTyping = (el) => !!el && (el.isContentEditable ||
      /^(input|textarea|select)$/i.test(el.tagName || ''));
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'h' && e.key !== 'H') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target) || isTyping(document.activeElement)) return;
      e.preventDefault();
      setHidden(root.getAttribute('data-al-chrome') !== 'hidden');
      dismissHint();
    });

    // --- the one that needs no learning --------------------------------------
    // Fine pointers only: on touch there is no "idle mouse", and a control that
    // vanished on its own would just look broken.
    const fine = !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
    let idleTimer = 0;
    const wake = () => {
      root.removeAttribute('data-al-idle');
      if (!fine) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        // Never fade the chrome out from under an open dialog or someone typing - the
        // controls are the thing being used at that moment.
        if (document.querySelector('#al-dl-modal:not([hidden])')) return;
        if (isTyping(document.activeElement)) return;
        root.setAttribute('data-al-idle', '1');
      }, ${CHROME_IDLE_MS});
    };
    for (const ev of ['pointermove', 'pointerdown', 'keydown', 'wheel', 'touchstart']) {
      window.addEventListener(ev, wake, { passive: true });
    }
    wake();

    // --- discoverability ------------------------------------------------------
    // Once per browser, and only where the key is usable. Silent on touch, and silent
    // on a page with no chrome to hide (a video artefact suppresses the Download pill,
    // so the hint would be advertising nothing).
    const hint = document.getElementById('al-chrome-hint');
    let hintTimer = 0;
    function dismissHint() {
      if (!hint) return;
      hint.removeAttribute('data-show');
      clearTimeout(hintTimer);
      write(HINT_KEY, '1');
    }
    if (hint && fine && read(HINT_KEY) !== '1' &&
        document.querySelector(${JSON.stringify(PRESENTER_CHROME_SELECTORS)})) {
      hint.setAttribute('data-show', '1');
      hintTimer = setTimeout(dismissHint, 6000);
      hint.addEventListener('click', dismissHint);
    } else if (hint) {
      // Nothing to say - make sure it can never be reached by a screen reader.
      hint.remove();
    }
  })();`;
}

/** base62 — URL-safe, no separators an agent might mangle. */
const SLUG_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const DEFAULT_SLUG_LENGTH = 16;

/**
 * Generate an unguessable slug. Slug entropy is the access control for public
 * artifacts (the public-by-unguessable-URL model), so this must be CSPRNG-backed, not Math.random.
 * Uses `crypto.getRandomValues` (available in Node 20+ and Workers) with
 * rejection sampling so the base62 mapping is unbiased.
 */
export function generateSlug(length = DEFAULT_SLUG_LENGTH): string {
  if (length < 12) {
    throw new Error('Augmented Live slug must be at least 12 chars for adequate entropy');
  }
  // Largest multiple of 62 that fits in a byte; bytes >= it are rejected to
  // keep the distribution flat (256 % 62 != 0).
  const ceiling = 256 - (256 % SLUG_ALPHABET.length); // 248
  let out = '';
  const buf = new Uint8Array(length * 2); // over-draw to limit re-fills
  while (out.length < length) {
    crypto.getRandomValues(buf);
    for (let i = 0; i < buf.length && out.length < length; i++) {
      const b = buf[i]!;
      if (b >= ceiling) continue; // reject to avoid modulo bias
      out += SLUG_ALPHABET[b % SLUG_ALPHABET.length];
    }
  }
  return out;
}

/** S3 key for an immutable artifact body at a given version. */
export function contentObjectKey(slug: string, version: number): string {
  return `${slug}/content/${version}.html`;
}

/**
 * S3 key for the mutable per-artifact editing-state object (ENG-6838). The viewer
 * shell fetches this on load so a link-first page opened while the agent is still
 * working shows the "Updating…" overlay immediately - the fire-and-forget realtime
 * `editing` broadcast is missed by a page that subscribes after it was sent.
 * Written no-cache; carries `{ editing: boolean, agentName?, agentAvatar? }`.
 */
export function stateObjectKey(slug: string): string {
  return `${slug}/state.json`;
}

/**
 * S3 key for the per-artifact viewer shell. Stored at the *bare* slug so the
 * canonical URL `/{slug}` maps to it directly with no CloudFront rewrite (S3's
 * flat keyspace lets `{slug}` and the `{slug}/…` content prefix coexist).
 */
export function shellObjectKey(slug: string): string {
  return slug;
}

/**
 * S3 key for the NO-FOOTER premium shell variant (ENG-7469 / ADR-0042). A sibling
 * under the slug prefix - safe because the content object lives at
 * `{slug}/content/…`, so `{slug}/__nofooter` never collides. The edge (ENG-7468
 * viewer-request Function) rewrites `/{slug}` -> this key when the org resolves
 * premium; the bare-slug shell (with the attribution footer) is the default,
 * fail-closed serve. `planPublish` writes BOTH so the premium rewrite never 404s.
 */
export function premiumShellObjectKey(slug: string): string {
  return `${slug}/__nofooter`;
}

/** Absolute URL of an immutable content object on the CDN. */
export function contentUrl(cdnDomain: string, slug: string, version: number): string {
  return `https://${stripScheme(cdnDomain)}/${contentObjectKey(slug, version)}`;
}

/** Canonical public URL of the artifact (serves the shell). */
export function buildLiveUrl(cdnDomain: string, slug: string): string {
  return `https://${stripScheme(cdnDomain)}/${slug}`;
}

/** Matches a `.al-item` class attribute (the fixed-size carousel/deck canvas). */
const AL_ITEM_CLASS_RE = /class\s*=\s*("|')[^"']*\bal-item\b[^"']*\1/i;

/**
 * Width of the right-hand shell panel (chat / stats), in CSS px (ENG-8510).
 *
 * Exported so the CSS in the shell and the JS that computes the canvas inset are
 * derived from ONE number. They used to be able to drift, which is how a panel
 * ends up covering a slice of canvas nobody accounted for.
 */
export const AL_PANEL_WIDTH_PX = 420;

/**
 * Narrowest canvas we are willing to leave beside an open panel, in CSS px.
 *
 * Below this the split stops being useful: the fit-to-window scaler would shrink
 * a 1080px slide into the remainder, and at ~200px that is a ~18% zoom - text
 * too small to read. We would rather cover the canvas for the duration of the
 * conversation than "show" it at a size nobody can use.
 */
export const AL_MIN_CANVAS_PX = 320;

/**
 * Viewport width at/above which the shell splits (canvas + panel side by side)
 * rather than letting the panel take the whole screen (ENG-8510, small-viewport
 * decision). Below it, the panel goes full-width and the canvas keeps its full
 * size underneath, un-shrunk, ready to be revealed again on close.
 */
export const AL_SPLIT_MIN_VIEWPORT_PX = AL_PANEL_WIDTH_PX + AL_MIN_CANVAS_PX;

/**
 * Canvas inset for a given viewport width and panel-open state (ENG-8510).
 *
 * This is the whole small-viewport policy in one testable function: how many px
 * of width the open right-hand panel takes away from the canvas iframe. `0` means
 * "do not shrink the canvas" - either nothing is open, or the viewport is too
 * narrow to split and the panel is covering it full-width instead.
 */
export function canvasInsetPx(viewportWidth: number, panelOpen: boolean): number {
  if (!panelOpen) return 0;
  if (!Number.isFinite(viewportWidth) || viewportWidth < AL_SPLIT_MIN_VIEWPORT_PX) return 0;
  return AL_PANEL_WIDTH_PX;
}

/**
 * Matches a Remotion `<Player>` artefact: the `@remotion/player` import is the
 * load-bearing signature (the player IS the in-browser playback — ENG-6776), so
 * any video body references it and no other artefact does. Used to suppress the
 * Download-PDF overlay on videos (a PDF export of a video is meaningless and
 * video export isn't supported) and to keep the overlay clear of the player's
 * bottom playback bar (ENG-7085).
 */
const REMOTION_PLAYER_RE = /@remotion\/player/i;

/**
 * ENG-8518: the Remotion-video sniff, shared rather than copied.
 *
 * planPublish and the shell reconciler must agree on what counts as a video, or
 * a re-render would re-add the Download-PDF control to an artefact the publish
 * path deliberately suppressed it for, and shift the chrome back over the
 * player's controls bar. Two regexes that drifted would produce exactly that,
 * and only on already-published artefacts - the hardest place to notice it.
 */
export function isRemotionVideoHtml(html: string | null | undefined): boolean {
  return REMOTION_PLAYER_RE.test(html ?? '');
}

/* ── ENG-8668: the deck navigation HUD ─────────────────────────────────────
 *
 * The attribution badge is `position:fixed; left:50%; bottom:14px` — and that is
 * exactly where the slide-deck skeleton in our OWN published Augmented Live
 * skill puts its prev/next HUD. Every free-tier deck built from the documented
 * pattern has its primary navigation control sitting under the platform's badge,
 * and the author cannot fix it from their side: the badge is injected by this
 * shell and appears nowhere in their source.
 *
 * The badge's design note says it is bottom-centre "so it clears the bottom-left
 * Download pill and the bottom-right comment launcher". Those are the two
 * controls it was designed around. A CENTRED deck HUD was not one of them.
 */

/**
 * Geometry of the documented deck skeleton's HUD, in CSS px.
 *
 * These are not guesses — they are read off the skeleton in
 * `packages/supabase/seeds/integration-definitions.json`
 * (`skill-agt-live:build-slide-deck`):
 *
 *   #hud        { bottom: 20px; padding: 8px 14px; border: 1px }
 *   #hud button { font-size: 22px; line-height: 1; padding: 4px 10px }
 *
 * so the tallest child is 22 + 4 + 4 = 30px, and the pill is
 * 30 + 8 + 8 (padding) + 1 + 1 (border) = 48px tall, spanning 20 → 68px.
 *
 * `eng-8668-deck-hud-collision.test.ts` re-derives all of this FROM the seed
 * JSON and fails if these numbers stop matching the shipped skeleton. That test
 * is the "one place" AC3 asks for: the skeleton and the shell badge are coupled
 * by an assertion rather than by a comment, so moving either one without the
 * other turns CI red instead of silently re-creating the overlap.
 */
export const DECK_HUD_BOTTOM_PX = 20;
export const DECK_HUD_HEIGHT_PX = 48;
/** Top edge of the HUD — the line the badge has to clear. */
export const DECK_HUD_TOP_PX = DECK_HUD_BOTTOM_PX + DECK_HUD_HEIGHT_PX; // 68

/** Breathing room between the HUD and the badge lifted above it. */
const DECK_CHROME_GAP_PX = 8;

/** Where the badge sits on a deck: clear of the HUD's top edge. */
export const DECK_ATTRIBUTION_BOTTOM_PX = DECK_HUD_TOP_PX + DECK_CHROME_GAP_PX; // 76

/**
 * On a narrow viewport the lifted badge would land on the bottom-LEFT Download
 * pill (`#al-download`, `bottom:72px`, ~32px tall ⇒ 72→104px), because a
 * ~178px-wide centred badge and a ~130px-wide left-anchored pill stop clearing
 * each other horizontally below roughly 470px of viewport width. Lift the badge
 * over the pill as well rather than relocating the Download control — the badge
 * is the element this ticket is about, and moving somebody else's control to fix
 * ours is the wider blast radius.
 *
 * 560px rather than the file's usual 460px breakpoint: the crossover computes to
 * ~470px from ESTIMATED pill widths, and the cheap direction to be wrong in is
 * "nudged when it did not strictly need to be".
 */
const DECK_NARROW_MAX_WIDTH_PX = 560;

/**
 * The Download pill's geometry, NAMED so the badge below can be derived from it.
 *
 * CodeRabbit on `#6409`: `DECK_ATTRIBUTION_NARROW_BOTTOM_PX` was `104 + gap`, where 104 was
 * `72 + ~32` written as a magic number. CS-1763 gave the pill a 44px mobile touch target,
 * so it now reaches 116 - and the badge, still derived from the stale 104, sat at 112 and
 * OVERLAPPED IT BY 4PX. The derivation was correct when written and nothing connected it to
 * the value it depended on.
 *
 * Deriving it means the next person who changes the pill's height moves the badge with it,
 * or fails a test that reads both out of the rendered stylesheet.
 */
export const DOWNLOAD_BOTTOM_PX = 72;
/** Intrinsic height: 13px text + .5rem padding. */
export const DOWNLOAD_INTRINSIC_HEIGHT_PX = 32;
/** CS-1763: the standard minimum touch target, applied below 640px. */
export const DOWNLOAD_MOBILE_MIN_HEIGHT_PX = 44;

/**
 * Above the Download pill's top edge plus the same gap.
 *
 * Uses the MOBILE height because this constant only applies below
 * DECK_NARROW_MAX_WIDTH_PX (560), which is inside the 640px touch-target query - so the
 * pill is 44 tall wherever this value is used, never 32.
 */
export const DECK_ATTRIBUTION_NARROW_BOTTOM_PX =
  DOWNLOAD_BOTTOM_PX + DOWNLOAD_MOBILE_MIN_HEIGHT_PX + DECK_CHROME_GAP_PX; // 124

/**
 * The documented deck skeleton, sniffed structurally.
 *
 * Requires BOTH the `deck-js` root-class marker the skeleton's bootstrap sets
 * and its `id="hud"` element. Either alone is too loose — `hud` is a plausible
 * id in unrelated artefact HTML — and together they are specific to this
 * skeleton.
 *
 * This exists because `kind === 'deck'` is NOT sufficient. `kind` is
 * agent-supplied and defaults to `'other'` (see `augmented-live-dispatch.ts`),
 * and the same skeleton is also published as `'carousel'`. A deck built exactly
 * from the documented pattern and published with the default kind would keep the
 * collision the ticket is about, which is the case most likely to occur in the
 * wild.
 */
const DECK_HUD_RE = /\bdeck-js\b/i;
const DECK_HUD_ID_RE = /id\s*=\s*("|')hud\1/i;

/**
 * The deck-skeleton sniff, shared rather than copied — same reasoning as
 * `isRemotionVideoHtml` above. `planPublish` and the shell reconciler must agree
 * on what counts as a deck, or a re-render would drop the badge back onto the
 * HUD for already-published artefacts, which is the hardest place to notice it.
 */
export function hasDeckNavHud(html: string | null | undefined): boolean {
  const h = html ?? '';
  return DECK_HUD_RE.test(h) && DECK_HUD_ID_RE.test(h);
}

/** Copy for the Download dialog, chosen by artefact kind. */
export interface DownloadCopy {
  /** `title` on the Download pill. */
  title: string;
  /** `<h2>` in the confirmation dialog. */
  heading: string;
  /** Body line in the confirmation dialog. */
  description: string;
}

/**
 * CS-1615: the Download dialog used to say "LinkedIn-ready carousel" on every
 * artefact, whatever it was. Samuel hit it on a formal quotation published as
 * `kind: 'site'` and going to a customer's procurement team.
 *
 * THE DEFAULT MUST BE THE NEUTRAL COPY, and that is the whole design of this
 * function rather than an accident of ordering. `kind` is agent-supplied and
 * defaults to `'other'` (see the `isVideo`/`isDeck` gates below, which carry the
 * same caveat), so it is a HINT, not a fact. Making the LinkedIn phrasing opt-in
 * behind an explicit `carousel` means an unreliable signal fails to "a carousel
 * gets neutral copy" — invisible and harmless — instead of "a quotation is
 * called a carousel", which is the reported bug and happens in front of a client.
 *
 * When a signal cannot be trusted, default to the wording that is never wrong.
 */
export function describeDownload(kind: string | undefined, isDeck: boolean): DownloadCopy {
  if (kind === 'carousel') {
    return {
      title: 'Download as PDF (LinkedIn carousel)',
      heading: 'Download this carousel',
      description: 'Save the full carousel as a PDF, one page per slide (LinkedIn-ready).',
    };
  }
  // The deck sniff is content-based and therefore more trustworthy than `kind`,
  // so it is honoured even when `kind` is absent — same reasoning as `isDeck`.
  if (isDeck || kind === 'deck') {
    return {
      title: 'Download as PDF',
      heading: 'Download this deck',
      description: 'Save the full deck as a PDF, one page per slide.',
    };
  }
  return {
    title: 'Download as PDF',
    heading: 'Download this page',
    description: 'Save the full asset as a PDF.',
  };
}

/**
 * How long the download dialog holds its busy state before giving up and
 * dismissing itself.
 *
 * There is no event for "an attachment download has started" — the browser
 * cancels the navigation when it reads `Content-Disposition: attachment` and
 * fires nothing — so the reset has to be a timer, and the only principled value
 * comes from the server's own bound. The API waits `PUBLIC_EXPORT_GRACE_MS`
 * (10s) and then serves the interstitial, which REPLACES this page. So by
 * grace-plus-a-margin either the file has arrived or this document is gone.
 *
 * The dangerous direction is the server's window growing past this one: the
 * busy state would clear while the server was still waiting, returning the
 * reader to precisely the unexplained blank this exists to prevent. That is
 * pinned from the api side by
 * `packages/api/src/__tests__/eng-10468-busy-reset-outlasts-grace.test.ts`,
 * which imports both numbers rather than restating either.
 */
export const PUBLIC_EXPORT_BUSY_RESET_MS = 12_000;

/**
 * ENG-10468: the download dialog's client script.
 *
 * Exported so the behaviour test can EXECUTE the real shipped string against a
 * stub DOM, rather than assert `.includes(...)` against a copy of it — the same
 * reason `deckChromeOverrideCss` below is exported.
 *
 * ## Why the dialog now stays open
 *
 * `GET /artifacts/:slug/export` is a SINGLE BLOCKING REQUEST. The route starts
 * or attaches the export job and waits up to `PUBLIC_EXPORT_GRACE_MS` polling
 * the row; only when it gives up does it serve the explanatory interstitial.
 * Inside that window nothing has been sent, so there is nothing for the browser
 * to render and no way for the interstitial to arrive sooner — it explains a
 * wait it necessarily arrives after.
 *
 * The previous handler dismissed the dialog on the next tick ("let the browser
 * start the download, then dismiss it"), which reads as correct and is, for a
 * fast export. Measured 2026-09-14: a 5,919,052-byte PDF rendered in 9,228 ms,
 * just inside the window. So the dialog vanished a tick after the click and the
 * reader sat on the unchanged artefact for nine seconds with no signal at all —
 * strictly less feedback than leaving it open would have given.
 *
 * The click is therefore acknowledged here, synchronously, and the dialog is
 * held open carrying the explanation until the wait is necessarily over.
 *
 * ## What must not change
 *
 * The anchor is CROSS-ORIGIN (`exportBase` is a different origin), so the
 * `download` attribute is ignored and the browser NAVIGATES. It is the route's
 * `Content-Disposition: attachment` that turns that navigation into a download
 * and leaves this page standing. Which is why the FIRST click must never be
 * given `preventDefault()`: swallow it and the export stops working outright.
 * Only a repeat click while already busy is suppressed, and that is a cost
 * control — `exportRateLimit` allows 10 requests per 5 minutes per IP.
 *
 * Dismissing mid-wait (Cancel / Escape / backdrop) deliberately still works.
 * The navigation is already in flight and closing the dialog cannot recall it,
 * so refusing to close would trap the reader for no benefit.
 */
export function downloadDialogScript(): string {
  return `
  const dlBtn = document.getElementById('al-download');
  const dlModal = document.getElementById('al-dl-modal');
  if (dlBtn && dlModal) {
    const dlGo = document.getElementById('al-dl-go');
    const dlDesc = document.getElementById('al-dl-desc');
    const goIdle = dlGo ? dlGo.textContent : '';
    const descIdle = dlDesc ? dlDesc.textContent : '';
    let busy = false;
    let busyTimer;
    // Restore the resting copy. Safe to call when already idle, which is what
    // lets open/close reset unconditionally.
    const clearBusy = () => {
      busy = false;
      clearTimeout(busyTimer);
      if (dlGo) { dlGo.removeAttribute('aria-busy'); dlGo.textContent = goIdle; }
      if (dlDesc) dlDesc.textContent = descIdle;
    };
    const openDl = () => { clearBusy(); dlModal.hidden = false; dlGo?.focus(); };
    const closeDl = () => { clearBusy(); dlModal.hidden = true; dlBtn.focus(); };
    dlBtn.addEventListener('click', openDl);
    document.getElementById('al-dl-cancel')?.addEventListener('click', closeDl);
    dlModal.addEventListener('click', (e) => { if (e.target === dlModal) closeDl(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !dlModal.hidden) closeDl(); });
    dlGo?.addEventListener('click', (e) => {
      // A repeat click starts a SECOND export against a 10-per-5-minute IP
      // budget. The first click is never touched - see the header.
      if (busy) { e.preventDefault(); return; }
      busy = true;
      dlGo.setAttribute('aria-busy', 'true');
      dlGo.textContent = 'Preparing your file\u2026';
      if (dlDesc) dlDesc.textContent = 'Large exports can take a few seconds. Your download will start on its own - you can leave this open.';
      // Dismiss once the server's own wait is necessarily over.
      busyTimer = setTimeout(() => { clearBusy(); dlModal.hidden = true; }, ${PUBLIC_EXPORT_BUSY_RESET_MS});
    });
  }`;
}

/**
 * The CSS override applied for decks. Exported so the collision test can assert
 * against the real shipped string rather than a copy of it.
 */
export function deckChromeOverrideCss(): string {
  return `
  /* ENG-8668: lift the attribution badge clear of the documented deck
     skeleton's prev/next HUD (bottom:${DECK_HUD_BOTTOM_PX}px, ${DECK_HUD_HEIGHT_PX}px tall ⇒ top edge ${DECK_HUD_TOP_PX}px). */
  #al-attribution{bottom:${DECK_ATTRIBUTION_BOTTOM_PX}px}
  @media (max-width:${DECK_NARROW_MAX_WIDTH_PX}px){#al-attribution{bottom:${DECK_ATTRIBUTION_NARROW_BOTTOM_PX}px}}`;
}

/**
 * Inject a fit-to-window scaler into `.al-item` artifact content (ENG-6843,
 * ENG-6865). Carousel / deck / single-image bodies are FIXED-size canvases
 * (e.g. 1080×1350); with nothing scaling them the live viewer rendered them at
 * native size, so a 390px phone showed only ~36% of the slide and clipped the
 * rest. This appends a tiny script that, on load + resize, `zoom`s each
 * `.al-item` to fit the viewport on BOTH axes (`min(vw/w, vh/h)`, capped at 1:1 -
 * never upscale) and centres it. Fitting width AND height is what keeps a tall
 * portrait slide fully visible on a WIDE desktop window: width-only fitting left
 * `vw/w` at 1 there, so the 1350px slide overflowed the viewport height and you
 * had to scroll within a single slide (ENG-6865). Multiple items stack with a
 * gentle y scroll-snap so you move one slide at a time. No-op for free-form sites
 * (no `.al-item`) - those are authored responsive and the viewport meta already
 * handles them. Idempotent (skips if the scaler is already present). Injected into
 * the SERVED content object only, not source_html, so the recall copy stays the
 * agent's untouched HTML.
 */
export function injectFitToWindow(html: string): string {
  // Detect an agent-supplied fit-to-window fitter by its full SIGNATURE - a
  // scale/zoom assignment AND a resize listener AND a viewport read - rather than
  // a bare `scale(` anywhere (which would also match unrelated hover/animation
  // transforms and wrongly suppress injection, leaving the slide unscaled).
  const shipsOwnFitter =
    /\.style\.(?:transform\s*=\s*["'`]\s*scale\(|zoom\s*=)/.test(html) &&
    /addEventListener\(\s*["'`]resize["'`]/.test(html) &&
    /\binnerWidth\b|\binnerHeight\b/.test(html);
  // Skip when: not .al-item content; already injected (idempotent); or the agent
  // already ships their own fitter - injecting ours would double-scale the slide.
  if (!AL_ITEM_CLASS_RE.test(html) || html.includes('id="al-fit"') || shipsOwnFitter) {
    return html;
  }
  const snippet =
    '<style id="al-fit-style">html,body{margin:0}html{scroll-snap-type:y proximity}' +
    '.al-item{display:block;margin-left:auto;margin-right:auto;max-width:none;scroll-snap-align:center}' +
    // ENG-6895: same intra-slide overflow guard the PNG/PDF render applies via
    // itemStylesheet(), mirrored here so the live viewer matches the exported
    // image. Lets an over-wide big-number stat wrap instead of overlapping its
    // neighbour; only engages when content would otherwise overflow.
    '.al-item :where(p,h1,h2,h3,h4,h5,h6,li,span,div){min-width:0;overflow-wrap:anywhere}</style>' +
    '<script id="al-fit">(function(){function f(){' +
    'var its=document.querySelectorAll(".al-item");if(!its.length)return;' +
    'var de=document.documentElement,vw=de.clientWidth,vh=de.clientHeight;' +
    'for(var i=0;i<its.length;i++){var it=its[i];it.style.zoom="";' +
    'var w=it.offsetWidth||1080,h=it.offsetHeight||1080;' +
    'it.style.zoom=String(Math.min(1,vw/w,vh/h));}}' +
    'f();addEventListener("resize",f);addEventListener("load",f);})();</script>';
  return /<\/body>/i.test(html) ? html.replace(/<\/body>/i, `${snippet}</body>`) : html + snippet;
}

/**
 * CS-1634 item 5 — the default print stylesheet a contract actually needs.
 *
 * What produced a usable A4 Statement of Work was Samuel hand-writing a
 * `@media print` block: A4 page size, a page break per section, card chrome and
 * the grey body background stripped, and `break-inside: avoid` on table rows and
 * signature blocks so a payment schedule does not split across a page boundary
 * and a signature does not land alone. Every contract needs exactly that, and
 * every author was writing it from scratch — which is the whole reason a
 * five-minute job took an hour.
 *
 * This is the BROWSER-PRINT path (Cmd-P, and "Save as PDF" from the page). It is
 * distinct from the export renderer's `itemStylesheet()`, which paints a fixed
 * canvas for the Download button. Both exist deliberately: the renderer must be
 * pixel-deterministic for a carousel, whereas print should reflow like a
 * document. Making them one thing would force a contract to choose between
 * looking right on screen and printing correctly.
 *
 * ## Only documents, and only when the author has not decided for themselves
 *
 * Applied to `kind='site'` alone. A carousel or a deck printed at A4 with page
 * breaks per slide is not an improvement, it is a different wrong answer.
 *
 * Skipped entirely when the author already ships ANY `@media print` block, on
 * the same reasoning as the fit-to-window fitter above: an author who has
 * written print rules has a layout in mind, and injecting a second, later
 * ruleset would silently win on specificity ties and override the thing they
 * carefully wrote. Author-overridable means the author's block replaces this
 * one, not competes with it.
 *
 * Injected into the SERVED content only, never `source_html`, so the recall copy
 * stays the agent's untouched HTML. Idempotent.
 */
export function injectDocumentPrintStyles(html: string, kind?: string | null): string {
  if (kind !== 'site') return html;
  if (html.includes('id="al-print-style"')) return html;
  // Any author-authored print block, however spelled: a `@media print` at-rule
  // or a `<link media="print">`. Deliberately broad — a false NEGATIVE here
  // overrides an author's deliberate layout, which is worse than not helping.
  // CS-1634 review (CodeRabbit): `<style media="print">` is author print CSS too.
  // Matching only `@media print` and `<link media=print>` missed the third
  // spelling, so an author who wrote one got OUR stylesheet appended alongside
  // theirs - two rulesets competing on specificity ties, which is the surprise
  // "author-overridable" is supposed to rule out.
  if (
    /@media\s+print/i.test(html) ||
    // CS-1634 review (CodeRabbit on #5272): allow whitespace around `=`.
    // `media = "print"` is valid HTML and a real spelling; without `\s*` we
    // miss it, append our stylesheet alongside the author's, and produce the
    // two-competing-rulesets surprise this check exists to prevent. The whole
    // point of the check is that a false NEGATIVE overrides a deliberate layout.
    /<(?:link|style)[^>]+media\s*=\s*["']?[^"'>]*print/i.test(html)
  ) {
    return html;
  }

  const style =
    '<style id="al-print-style">@media print{' +
    // A4 with a real margin. The export canvas is edge-to-edge because it is a
    // fixed-size image; a printed contract needs a margin or the text runs into
    // the physical non-printable area of the sheet.
    '@page{size:A4;margin:18mm 16mm}' +
    // Strip the screen chrome. A grey page background prints as a grey slab and
    // burns toner; card shadows and rounded borders read as decoration on a
    // document that is meant to look like a contract.
    'html,body{background:#fff!important;margin:0!important;padding:0!important}' +
    '*{box-shadow:none!important;text-shadow:none!important}' +
    // One section per page, and let sections REFLOW rather than be pinned to a
    // fixed height the way the export canvas pins them. This is the difference
    // that stops print clipping the way the fixed-canvas export does.
    // `zoom:1!important` is load-bearing, not tidiness. injectFitToWindow sets
    // an INLINE `style.zoom` on every .al-item at runtime to fit the viewport,
    // and an inline style beats a stylesheet rule without !important. Without
    // this, a document printed from a narrow window prints SHRUNK to whatever
    // fraction the screen happened to need - a contract at 60% on the page.
    '.al-item{break-after:page;page-break-after:always;height:auto!important;' +
    'min-height:0!important;max-height:none!important;overflow:visible!important;' +
    'width:auto!important;zoom:1!important;box-sizing:border-box}' +
    '.al-item:last-child{break-after:auto;page-break-after:auto}' +
    // Keep the things a reader must see together, together. A payment schedule
    // row split across a page boundary, or a signature block orphaned onto a
    // page of its own, are the two that make a document look unprofessional and
    // in the second case arguably alter what was signed.
    // CS-1634 review (CodeRabbit on #5272): a SIGNATURE block split across a
    // page break is the one break that can actually undermine a contract - a
    // name on one sheet and the line it signs on another. Cheap to add, and
    // these are the class names the document markup already uses.
    'tr,img,figure,blockquote,.signature,.signature-block{break-inside:avoid;page-break-inside:avoid}' +
    'table{break-inside:auto}' +
    'thead{display:table-header-group}' +
    'h1,h2,h3,h4{break-after:avoid;page-break-after:avoid}' +
    // Widows/orphans: a single trailing line of a clause stranded on the next
    // page reads as a missing paragraph.
    'p,li{orphans:3;widows:3}' +
    // A link in a printed contract is dead, so show where it went — but only for
    // real external targets, never for in-page anchors or javascript: hrefs.
    'a[href^="http"]:after{content:" (" attr(href) ")";font-size:0.85em;word-break:break-all}' +
    '}</style>';
  return /<\/head\s*>/i.test(html)
    ? html.replace(/<\/head\s*>/i, `${style}</head>`)
    : /<\/body>/i.test(html)
      ? html.replace(/<\/body>/i, `${style}</body>`)
      : html + style;
}

/** Public realtime channel for a published artifact (carries `published` pings only). */
export function publicChannelName(slug: string): string {
  return `artifact:${slug}`;
}

/** Next monotonic version. First publish is v1. */
export function nextVersion(currentVersion?: number | null): number {
  return (currentVersion ?? 0) + 1;
}

export interface PlanPublishInput {
  /** Full HTML body of the artifact. */
  content: string;
  /** Optional human title (rendered into the shell <title> + tab). */
  title?: string;
  /**
   * Existing slug from a prior publish → UPDATE (URL preserved, version bumped).
   * Omitted → CREATE (new slug minted).
   */
  slug?: string;
  /** Current version of the existing row (UPDATE path). Ignored on CREATE. */
  currentVersion?: number | null;
  /**
   * Pre-minted slug for a CREATE. When the caller must know the slug *before*
   * planning (e.g. to externalize inline assets to {slug}/assets/ at publish —
   * ENG-6781), it mints one with generateSlug() and passes it here. Ignored on
   * UPDATE (slug supplied). Omitted ⇒ planPublish mints its own.
   */
  mintedSlug?: string;
  /** CDN host serving the bucket, e.g. `live.augmented.team` (no scheme). */
  cdnDomain: string;
  /** Public Supabase URL for the shell's realtime client. */
  supabaseUrl: string;
  /** Public Supabase anon key for the shell's realtime client. */
  supabaseAnonKey: string;
  /** Public API origin for the shell's Download button. Omitted ⇒ no button. */
  exportBase?: string;
  /**
   * Public webapp origin (e.g. `https://app.augmented.team`) that hosts the
   * /live-bridge relay. When set, the shell loads its content via srcDoc with
   * the selection bridge injected and arms the authenticated comment overlay
   * (ENG-6788). Omitted ⇒ the shell stays read-only with the legacy `src`
   * loader (no extra fetch, no overlay) - the feature is per-stage opt-in.
   */
  appBase?: string;
  /**
   * Agent-declared artefact kind (`published_artefacts.kind`: 'video', 'deck',
   * 'app', …). ENG-7088: passed through to the shell so video-specific behaviour
   * (Download-PDF suppression) keys off the explicit kind as well as the
   * `@remotion/player` content-sniff. Omitted ⇒ kind unknown (sniff only).
   */
  kind?: string;
}

export interface PlannedObject {
  key: string;
  body: string;
  contentType: string;
  cacheControl: string;
}

export interface PublishPlan {
  slug: string;
  version: number;
  liveUrl: string;
  isUpdate: boolean;
  /** S3 objects to PUT (content body + regenerated shell). */
  objects: PlannedObject[];
  /** Realtime broadcast to emit after the writes commit. */
  broadcast: {
    channel: string;
    event: 'published';
    payload: { slug: string; version: number };
  };
}

/**
 * Compute the full set of side effects for a publish/re-publish. Pure — the
 * caller executes the S3 writes, DB upsert, and broadcast.
 */
export function planPublish(input: PlanPublishInput): PublishPlan {
  const isUpdate = typeof input.slug === 'string' && input.slug.length > 0;
  // An UPDATE (slug supplied) must carry the prior version so the new content
  // lands at version+1. Without it, nextVersion() falls back to 1 and would
  // overwrite the immutable content/1.html object — refuse rather than clobber.
  // The caller resolves currentVersion from the slug's existing row; a null
  // here means the slug doesn't exist for this team, so the update is invalid.
  if (
    isUpdate &&
    !(
      typeof input.currentVersion === 'number' &&
      Number.isInteger(input.currentVersion) &&
      input.currentVersion > 0
    )
  ) {
    throw new Error(
      `planPublish: slug "${input.slug}" was supplied for an update but currentVersion is ` +
        `missing or invalid (${String(input.currentVersion)}); refusing to overwrite immutable content.`,
    );
  }
  const slug = isUpdate ? input.slug! : (input.mintedSlug ?? generateSlug());
  const version = isUpdate ? nextVersion(input.currentVersion) : 1;

  // ENG-7469 (ADR-0042): render BOTH shell variants. They differ only in the
  // attribution footer, so share every other input. The footered shell is the
  // default (bare-slug) object the edge serves on a free / fail-closed resolve;
  // the no-footer variant is written to the premium key and the edge rewrites to
  // it for premium serves (ENG-7468). Writing both at publish keeps the premium
  // rewrite from ever hitting a missing object.
  const shellArgs: RenderShellInput = {
    slug,
    version,
    cdnDomain: input.cdnDomain,
    title: input.title,
    supabaseUrl: input.supabaseUrl,
    supabaseAnonKey: input.supabaseAnonKey,
    exportBase: input.exportBase,
    appBase: input.appBase,
    // ENG-7496: `.al-item` OR a deck-js deck. Asking AL_ITEM_CLASS_RE directly
    // here is what let the shell disagree with the item manifest about the same
    // body; `hasRenderableItems` is the single predicate both now ask.
    hasItems: hasRenderableItems(input.content),
    isVideo: REMOTION_PLAYER_RE.test(input.content),
    // ENG-8668: sniffed here AND in the reconciler, via the same shared helper,
    // so a re-render of an already-published deck reaches the same verdict.
    isDeck: hasDeckNavHud(input.content),
    kind: input.kind,
  };
  const shell = renderShell({ ...shellArgs, footer: true });
  const premiumShell = renderShell({ ...shellArgs, footer: false });

  return {
    slug,
    version,
    liveUrl: buildLiveUrl(input.cdnDomain, slug),
    isUpdate,
    objects: [
      {
        key: contentObjectKey(slug, version),
        body: input.content,
        contentType: HTML_CONTENT_TYPE,
        cacheControl: CONTENT_CACHE_CONTROL,
      },
      {
        key: shellObjectKey(slug),
        body: shell,
        contentType: HTML_CONTENT_TYPE,
        cacheControl: SHELL_CACHE_CONTROL,
      },
      {
        key: premiumShellObjectKey(slug),
        body: premiumShell,
        contentType: HTML_CONTENT_TYPE,
        cacheControl: SHELL_CACHE_CONTROL,
      },
    ],
    broadcast: {
      channel: publicChannelName(slug),
      event: 'published',
      payload: { slug, version },
    },
  };
}

export interface RenderShellInput {
  slug: string;
  version: number;
  cdnDomain: string;
  title?: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  /**
   * Public origin of the Augmented API (e.g. `https://api.augmented.team`),
   * baked in so the shell can overlay a Download button linking to
   * `{exportBase}/artifacts/{slug}/export`. Omitted ⇒ no Download button
   * (export not configured for the stage). Design: docs/design/augmented-live-image-export.md
   */
  exportBase?: string;
  /**
   * Public webapp origin hosting the /live-bridge relay (ENG-6788). When set,
   * the shell loads content via srcDoc with the selection bridge injected and
   * arms the authenticated comment overlay. Omitted ⇒ legacy read-only shell.
   */
  appBase?: string;
  /**
   * Whether the artifact body uses the `.al-item` convention (carousel / deck).
   * When true the og:image is pinned to `item=0` so the social card shows only
   * the first slide rather than a tall stacked strip (ENG-6884). Free-form
   * bodies (no `.al-item`) leave this false and get the whole-page preview.
   */
  hasItems?: boolean;
  /**
   * Whether the artefact is a Remotion `<Player>` video (detected by the
   * `@remotion/player` import). When true the Download-PDF overlay is omitted —
   * a PDF export of a video is meaningless (and unsupported, ENG-6776) and the
   * button otherwise sits over the player's bottom playback bar (ENG-7085).
   */
  isVideo?: boolean;
  /**
   * Whether the artefact body is a deck built from the documented skeleton, i.e.
   * one carrying a centred prev/next HUD at the bottom (ENG-8668). When true the
   * attribution badge is lifted clear of it. Content-sniffed rather than trusted
   * from `kind`, because `kind` is agent-supplied and defaults to `'other'` —
   * see `hasDeckNavHud`.
   */
  isDeck?: boolean;
  /**
   * Agent-declared artefact kind (ENG-7088). The Download-PDF suppression treats
   * `kind === 'video'` as video too — belt-and-suspenders with the `isVideo`
   * content-sniff, so a video the sniff misses (e.g. a lazy-loaded player) is
   * still covered, and vice-versa.
   */
  kind?: string;
  /**
   * ENG-7469 (ADR-0042): stamp the light "Made with Ninjafy" attribution
   * footer (free tier). Omitted / false ⇒ no footer (the premium variant). The
   * tier is NOT decided here - `planPublish` renders BOTH a footered shell (the
   * default, fail-closed) and a no-footer premium variant, and the edge picks at
   * serve time (ENG-7468). Attribution is marketing, not a paywall.
   */
  footer?: boolean;
  /**
   * ENG-7470 (ADR-0042 Section 5): the free-tier sunset countdown banner. When
   * set, the shell shows "This artefact will be deleted on {date}. Upgrade to
   * Premium to retain." during the 7-day grace window (days 30-37). Baked by the
   * retention sweep re-rendering the free `{slug}` shell at the sunset boundary;
   * the premium `__nofooter` variant is never given a banner, so a premium serve
   * (or an upgrade) shows the clean page. Value is a display date (e.g. "12 Aug 2026").
   */
  sunsetDeleteOn?: string;
}

/** Input for the expired-artefact placeholder page (ENG-7470). */
export interface RenderExpiredPlaceholderInput {
  /** The artefact title, if any, for the page <title>. */
  title?: string;
}

/**
 * ENG-7470 (ADR-0042 Section 5): the day-37 "this page has expired" placeholder.
 *
 * A minimal, standalone 200 page (NOT a 404, and none of the viewer-shell
 * machinery - no sandboxed iframe, no realtime client, no analytics beacon). The
 * retention sweep overwrites the free artefact's shell object with this at
 * expiry, so an already-shared link never dead-ends; it invites the visitor to
 * create their own. (A free artefact's content is hard-deleted at expiry, v1.)
 */
export function renderExpiredPlaceholder(input: RenderExpiredPlaceholderInput = {}): string {
  const titleText = input.title?.trim() ? input.title.trim() : 'Augmented Live';
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="robots" content="noindex">
<title>${htmlEscape(titleText)} - expired</title>
<style>
  :root{color-scheme:light}
  *{box-sizing:border-box}
  html,body{margin:0;height:100%}
  body{display:flex;align-items:center;justify-content:center;padding:24px;
    font-family:system-ui,-apple-system,sans-serif;color:#0b1020;
    background:radial-gradient(120% 120% at 50% 0%,#f5f7fb 0%,#e9edf5 100%)}
  main{max-width:440px;text-align:center}
  h1{margin:0 0 10px;font-size:22px;font-weight:800}
  p{margin:0 0 22px;font-size:15px;line-height:1.5;color:#475069}
  a.cta{display:inline-flex;align-items:center;text-decoration:none;font-size:14px;
    font-weight:700;color:#fff;background:#0b1020;padding:.7rem 1.2rem;border-radius:10px;
    box-shadow:0 2px 10px rgba(0,0,0,.2)}
  a.cta:hover{background:#1b2236}
</style>
</head>
<body>
<main>
<h1>This page has expired</h1>
<p>This Augmented Live page is no longer available. Free pages are kept for a limited time. Upgrade to Premium to keep your pages online permanently.</p>
<a class="cta" href="https://augmented.team/?utm_source=augmented-live&utm_medium=expired" target="_blank" rel="noopener noreferrer">Make your own with Augmented Team</a>
</main>
</body>
</html>`;
}

/**
 * Render the viewer shell. The artifact body runs in a **sandboxed** iframe
 * (no `allow-same-origin`) so agent-generated JS is isolated from the shell's
 * realtime client and our domain's cookies. The shell subscribes to the
 * public channel and, on a `published` ping with a higher version, repoints
 * the iframe at the new immutable content object (cache-busted by path).
 *
 * When `appBase` is set the shell additionally (a) loads the content via
 * srcDoc with the selection bridge injected (so an authenticated viewer can
 * select text) and (b) arms a comment overlay that relays the comment to the
 * agent through the same-origin /live-bridge iframe (ENG-6788). Without it the
 * legacy `src`-based read-only shell is emitted unchanged.
 */
export function renderShell(input: RenderShellInput): string {
  const initialContent = contentUrl(input.cdnDomain, input.slug, input.version);
  const titleText = input.title?.trim() ? input.title.trim() : 'Augmented Live';
  // JSON.stringify yields a safe JS string literal for embedding in <script>;
  // also guard the </script> break-out.
  const cfg = JSON.stringify({
    slug: input.slug,
    version: input.version,
    base: `https://${stripScheme(input.cdnDomain)}/${input.slug}`,
    supabaseUrl: input.supabaseUrl,
    anonKey: input.supabaseAnonKey,
    channel: publicChannelName(input.slug),
    // ENG-7057: API origin for the anonymous view-analytics beacon. Null ⇒ the
    // beacon is inert (analytics not configured for the stage).
    api: input.exportBase ? input.exportBase.replace(/\/+$/, '') : null,
  }).replace(/</g, '\\u003c');

  // Download button (Step 1). A plain cross-origin `<a download>`: the export
  // route sets Content-Disposition: attachment, so the browser downloads even
  // though the `download` attribute is ignored cross-origin. No fetch ⇒ no CORS.
  // The button lives in the SHELL (not the sandboxed content iframe), so it
  // never has to cross the sandbox boundary — it just links to the render route.
  // ENG-7097 / ENG-7054: the Download control opens a confirmation modal; the
  // real export link lives inside it and only fetches when the user clicks
  // "Download PDF" there. The export URL is the same render route — the API
  // 302-redirects large (>6MB) renders to the CDN copy (no Lambda size ceiling).
  // ENG-7085: the whole control (button + modal) is suppressed for Remotion video
  // artefacts — a PDF export of a video is meaningless, and the button would
  // otherwise sit over the player's bottom playback bar.
  const exportUrl = input.exportBase
    ? `${input.exportBase.replace(/\/+$/, '')}/artifacts/${input.slug}/export?format=pdf&v=${input.version}`
    : '';
  // ENG-7088: a video is anything the @remotion/player sniff flags OR an artefact
  // explicitly published as kind='video' — either signal suppresses the button.
  const isVideo = input.isVideo === true || input.kind === 'video';
  // ENG-7691: Remotion <Player> renders a ~52px controls bar at the very bottom of
  // the player, which fills the viewport on 16:9 screens. The fixed-position shell
  // chrome (attribution footer, chat launcher, stats button) sits at bottom:14–74px
  // and overlaps that bar. Nudge them all above the bar (68px = 52px bar + 16px gap)
  // for video artefacts via a CSS override block that follows the base declarations.
  const videoChromeOverrideCss = isVideo
    ? `
  /* ENG-7691: clear Remotion player controls bar (~52px) for video artefacts. */
  #al-attribution{bottom:68px}
  .al-launcher{bottom:68px}
  .al-stats-btn{bottom:126px}
  @media (max-width:460px){.al-stats-btn{bottom:116px}}`
    : '';
  // ENG-8668: a deck is the documented skeleton by content-sniff OR an artefact
  // explicitly published as kind='deck' — same belt-and-suspenders shape as the
  // video gate above, and necessary for the same reason: `kind` is agent-supplied
  // and defaults to 'other', while the sniff cannot see a deck that diverged from
  // the skeleton's markers. A video that is somehow also flagged as a deck keeps
  // the video override: the Remotion controls bar is real chrome the player
  // actually paints, whereas the deck HUD is only there if the skeleton is.
  const isDeck = !isVideo && (input.isDeck === true || input.kind === 'deck');
  const deckChromeCss = isDeck ? deckChromeOverrideCss() : '';
  // ENG-7692: JS snippet injected into portrait Remotion video content via srcDoc.
  // Detects a 9:16 (portrait) composition authored with an incorrect landscape
  // aspectRatio (e.g. '16 / 9' copied from the template) by inspecting the
  // composition canvas element's inline dimensions. When the Player container is
  // landscape-shaped for a portrait composition (height < expected by >10%), it
  // corrects the container's width/height/aspectRatio so the video fills the
  // viewport width instead of appearing as a narrow pillarboxed column with black
  // bars on the left and right. No-op for landscape (16:9) or correctly-styled
  // portrait compositions.
  const videoPortraitFixScript = isVideo
    ? '<script id="al-portrait-fix">' +
      '(function(){' +
      // f() tries to find and fix the portrait Player container.
      // Returns true when the Remotion composition canvas element is present
      // (fix applied or not needed), false when Remotion has not mounted yet
      // so the MutationObserver should keep watching.
      'function f(){' +
      'var r=document.getElementById("root");' +
      'if(!r||!r.firstElementChild)return false;' +
      // The Remotion Player's outer container is #root's immediate first child.
      'var p=r.firstElementChild;' +
      // Find the composition canvas: an absolutely-positioned child with a CSS
      // scale() transform and explicit pixel width/height (compositionWidth ×
      // compositionHeight). This is Remotion's internal scaling element.
      'var els=p.querySelectorAll("[style]");' +
      'var inner=null;' +
      'for(var i=0;i<els.length;i++){' +
      'var s=els[i].style;' +
      'if(s.position==="absolute"&&s.transform&&' +
      's.transform.indexOf("scale(")>=0&&' +
      's.width&&s.height){inner=els[i];break;}' +
      '}' +
      // Inner canvas not found yet — Remotion still loading.
      'if(!inner)return false;' +
      'var cw=parseFloat(inner.style.width);' +
      'var ch=parseFloat(inner.style.height);' +
      // No-op when composition is landscape or square (ch <= cw).
      'if(!cw||!ch||ch<=cw)return true;' +
      'var pw=p.offsetWidth;' +
      'var ph=p.offsetHeight;' +
      // Expected container height when the player correctly fills the width.
      'var expected=pw*ch/cw;' +
      // Mismatch >10% indicates the Player container was styled with the wrong
      // aspectRatio (e.g. 16:9 for a 9:16 composition). Correct it.
      'if(ph>0&&ph<expected*0.9){' +
      'p.style.width="100%";' +
      'p.style.height="auto";' +
      'p.style.aspectRatio=cw+"/"+ch;' +
      '}' +
      'return true;' +
      '}' +
      // MutationObserver watches until Remotion has mounted its composition
      // canvas (f() returns true), then disconnects. A 30s safety timeout
      // stops the observer if something unexpected prevents mounting.
      'var obs=null;var tid=null;' +
      'function tryAndStop(){if(f()){' +
      'if(obs){obs.disconnect();obs=null;}' +
      'if(tid){clearTimeout(tid);tid=null;}' +
      '}}' +
      'function startObs(){' +
      'obs=new MutationObserver(function(){tryAndStop();});' +
      'obs.observe(document.documentElement,' +
      '{childList:true,subtree:true,attributes:true,attributeFilter:["style"]});' +
      'tid=setTimeout(function(){if(obs){obs.disconnect();obs=null;}},30000);' +
      '}' +
      // On DOMContentLoaded: try immediately; if Remotion not yet mounted
      // (f()===false), start the observer to catch when it does.
      'function init(){if(!f())startObs();}' +
      'if(document.readyState==="loading"){' +
      'document.addEventListener("DOMContentLoaded",init);' +
      '}else{init();}' +
      // Re-run on resize to handle orientation changes; observer already
      // disconnected by this point so no double-work.
      'window.addEventListener("resize",function(){setTimeout(f,50);});' +
      '})();' +
      '</script>'
    : '';
  const videoPortraitFixLiteral = JSON.stringify(videoPortraitFixScript).replace(/</g, '\\u003c');
  const download = describeDownload(input.kind, isDeck);
  /**
   * CS-1763 part 2 - "open it" as distinct from "save it", for decks.
   *
   * A deck is authored on a fixed 1280x720 stage and the mobile fallback scales
   * TYPE down with the stage rather than reflowing, so dense slides are
   * microscopic on a phone. The PDF export already paginates one page per slide
   * and is the only fix that can reach decks ALREADY published - published
   * content objects are immutable, the shell is not - which is why this lands
   * here rather than in the slide DOM.
   *
   * `?disposition=inline` (part 1, PR #6406) is what makes the difference on the
   * paths that honour it: they stamped `attachment`, so tapping Download on a
   * phone saved to Files instead of opening the PDF viewer.
   *
   * NOT ALL PATHS HONOUR IT, and Obi Wan `b0ab39878805` is right that my earlier
   * wording ("both response paths") overclaimed. `/artifacts/:slug/export` has
   * four exits. Two read the param: the stored-inline read and the synchronous
   * render. Two do not:
   *   - the >6MB offload redirect serves the renderer-stamped object. Stated as
   *     a limit in the route itself, because that branch MUST redirect.
   *   - THE ASYNC JOB LANE redirects to `presignExportDownload`, which hardcodes
   *     `attachment` and takes no disposition input at all. It is reachable
   *     TODAY by default (`artefact-export-jobs-serve` defaults true), so a
   *     completed job row for the same artefact/version/format turns an inline
   *     request into a presigned `attachment` redirect.
   *
   * The job-lane case fails INVISIBLY: the reader taps Open, the phone saves to
   * Files, and the shell, the route and the logs all report success. Documented
   * here rather than silently, and threading the disposition through the presign
   * is the real fix - it is a change to a shared export contract in another
   * package, so it is its own ticket rather than a rider on this one.
   *
   * `exportUrl` already carries a query string, hence `&`. No `download`
   * attribute and `target="_blank"` on purpose: `download` on a cross-origin
   * href is what forces the save, and it is the thing being undone here.
   */
  const openInlineUrl = exportUrl ? `${exportUrl}&disposition=inline` : '';
  const openHtml =
    isDeck && openInlineUrl
      ? `<a id="al-dl-open" href="${htmlEscape(openInlineUrl)}" target="_blank" rel="noopener">Open</a>`
      : '';
  const downloadHtml = input.exportBase && !isVideo
    ? `<button id="al-download" type="button" aria-haspopup="dialog" aria-controls="al-dl-modal" title="${htmlEscape(download.title)}">Download</button>` +
      `<div id="al-dl-modal" role="dialog" aria-modal="true" aria-labelledby="al-dl-title" hidden>` +
      `<div id="al-dl-panel">` +
      `<h2 id="al-dl-title">${htmlEscape(download.heading)}</h2>` +
      `<p id="al-dl-desc">${htmlEscape(download.description)}</p>` +
      `<div id="al-dl-actions">` +
      `<button type="button" id="al-dl-cancel">Cancel</button>` +
      // Open sits BEFORE Download deliberately: on a phone, reading it is the
      // common case and saving it is the rare one, and the reverse order buries
      // the useful action behind the one that caused the complaint.
      openHtml +
      `<a id="al-dl-go" href="${htmlEscape(exportUrl)}" download>Download PDF</a>` +
      `</div></div></div>`
    : '';

  // ENG-7469 (ADR-0042 Section 1): the free-tier attribution footer. A light,
  // restrained "Made with Ninjafy" link - marketing attribution, not a
  // paywall (if stripped we've lost nothing). Bottom-center so it clears the
  // bottom-left Download pill and the bottom-right comment launcher; faded until
  // hover so it never occludes the artefact. Rendered only for the footered
  // (free) shell variant; premium serves the no-footer variant (edge-selected).
  //
  // ENG-10677 / ENG-10169: this href points at ninjafy.com BY AN EXPLICIT
  // OWNER DECISION (Brad, 2026-09-18), taken with the risk on the table and
  // restated after it was reverted once. Do not "fix" it back to
  // augmented.team; if you think it is wrong, raise it with him rather than
  // changing it.
  //
  // What the risk is, recorded so the decision stays legible: `ninjafy.com` is
  // classified malicious/phishing by 12+ AV vendors - inherited from the prior
  // owner's ad lander, not caused by anything we serve - and a named customer's
  // security team blocks it outright
  // (docs/runbooks/ninjafy-domain-reputation-delisting.md, ENG-10169, open).
  // SHELL_VERSION 7 re-renders the ENTIRE back catalogue, so this link reaches
  // free-tier artefacts customers have already published, and the apex is
  // currently a waitlist page rather than the product.
  //
  // The day-30 sunset banner and the day-37 tombstone below still point at
  // augmented.team; that split is known and deliberate for now.
  const footerHtml = input.footer
    ? `<a id="al-attribution" href="https://ninjafy.com/?utm_source=augmented-live&utm_medium=attribution" ` +
      `target="_blank" rel="noopener noreferrer">Made with Ninjafy</a>`
    : '';

  // ENG-7470: the sunset countdown banner (free tier, days 30-37). A restrained
  // top bar, not an occluding modal - the artefact stays fully usable during the
  // grace window. Baked by the sweep at the sunset boundary; the premium variant
  // never carries it, so an upgrade clears it at serve time (edge-selected).
  const sunsetHtml = input.sunsetDeleteOn
    ? `<div id="al-sunset" role="status">` +
      `<span>This page will be deleted on ${htmlEscape(input.sunsetDeleteOn)}.</span> ` +
      `<a href="https://augmented.team/?utm_source=augmented-live&utm_medium=sunset" ` +
      `target="_blank" rel="noopener noreferrer">Upgrade to Premium to keep it</a>` +
      `</div>`
    : '';

  // Social preview (og:image / twitter card). Points at the public thumbnail
  // route, so a shared live URL unfurls with a preview on LinkedIn / Slack.
  // For carousel / deck content (`.al-item` convention) we pin `item=0` so the
  // card shows just the FIRST slide; without it the renderer screenshots the
  // whole page and a multi-slide deck unfurls as one very tall stacked strip
  // (ENG-6884). Free-form sites have no `.al-item`, so we omit `item` and let
  // the renderer do its whole-page preview (forcing item=0 there would be an
  // out-of-range error). Gated on exportBase (the API origin that serves the
  // thumbnail), and version-pinned so a re-publish busts the crawler's cached card.
  const ogHtml = input.exportBase
    ? `<meta property="og:title" content="${htmlEscape(titleText)}">
<meta property="og:type" content="website">
<meta property="og:image" content="${htmlEscape(
        `${input.exportBase.replace(/\/+$/, '')}/artifacts/${input.slug}/thumbnail?${
          input.hasItems ? 'item=0&' : ''
        }v=${input.version}`,
      )}">
<meta name="twitter:card" content="summary_large_image">`
    : '';

  // ENG-6788 select-to-comment overlay. Gated on appBase (the webapp origin
  // that hosts /live-bridge); without it the legacy read-only shell is emitted.
  const markupEnabled = typeof input.appBase === 'string' && input.appBase.trim().length > 0;
  const appBase = markupEnabled ? input.appBase!.trim().replace(/\/+$/, '') : '';
  // The bridge is injected into the fetched content at view time (srcDoc) so
  // existing immutable content objects gain selection support with no
  // re-publish. Escape `<` so the bridge's own </script> can't break the
  // shell's module script out of its <script> tag.
  const bridgeLiteral = JSON.stringify(MARKUP_BRIDGE_SCRIPT).replace(/</g, '\\u003c');
  const markerLiteral = JSON.stringify(MARKUP_MARKER);
  const controlMarkerLiteral = JSON.stringify(MARKUP_CONTROL_MARKER);
  const appBaseLiteral = JSON.stringify(appBase).replace(/</g, '\\u003c');
  // The content loads via JS (srcDoc) whenever we need to inject something into
  // it: the markup bridge (ENG-6788), the analytics scroll/section reporter
  // (ENG-7057), or the portrait-video pillarbox fix (ENG-7692). With none of
  // these, keep the legacy static `src` so the read-only shell is byte-for-byte
  // unchanged. srcDoc is a same-origin fetch of the immutable content (same CDN
  // host) with a degrade-to-`src` fallback, so enabling it for video/analytics
  // shells reuses the proven markup mechanism.
  const needsSrcdoc = markupEnabled || !!input.exportBase || isVideo;
  const iframeSrcAttr = needsSrcdoc ? '' : ` src="${htmlEscape(initialContent)}"`;

  // ENG-7057: viewer-analytics beacon (shell-side dwell/session) + the in-content
  // scroll/section reporter injected on the srcdoc path. Both gated on exportBase
  // (the API origin); without it the beacon is omitted and the reporter literal
  // is the empty string, so the shell is unchanged for stages without analytics.
  const viewBeaconJs = input.exportBase ? VIEW_ANALYTICS_BEACON : '';
  const viewReporterLiteral = input.exportBase
    ? JSON.stringify(VIEW_REPORTER_SCRIPT).replace(/</g, '\\u003c')
    : '""';

  const markupStyles = markupEnabled
    ? `
  /* ENG-7108: 288px is wider than a 320px screen once the popover is offset from
     the tap point, so the send button fell off the right edge - the control that
     completes the action. min() keeps the desktop width and clamps on small
     screens rather than introducing a breakpoint that has to be kept in sync. */
  .al-cmt{position:fixed;z-index:2147483645;width:min(288px,calc(100vw - 24px));box-sizing:border-box;background:#fff;
    border:1px solid #e5e7eb;border-radius:8px;padding:8px;box-shadow:0 8px 24px rgba(0,0,0,.18);
    font-family:system-ui,-apple-system,sans-serif;font-size:13px;color:#111}
  .al-cmt-q{margin:0 0 8px;max-height:64px;overflow:hidden;border-left:2px solid #e5e7eb;
    padding-left:8px;font-size:12px;color:#6b7280;white-space:pre-wrap}
  .al-cmt-ta{width:100%;box-sizing:border-box;resize:none;border:1px solid #e5e7eb;border-radius:6px;
    padding:6px;font:inherit;outline:none}
  .al-cmt-ta:focus{border-color:#10b981}
  .al-cmt-err{margin:6px 0 0;color:#dc2626;font-size:12px}
  .al-cmt-ok{margin:0;padding:6px 2px;color:#059669;font-size:12px}
  .al-cmt-row{display:flex;justify-content:flex-end;gap:8px;margin-top:8px}
  .al-cmt-cancel{background:none;border:0;color:#6b7280;font:inherit;cursor:pointer;padding:4px 8px;border-radius:6px}
  .al-cmt-send{background:#059669;border:0;color:#fff;font:inherit;font-weight:600;cursor:pointer;
    padding:4px 10px;border-radius:6px}
  .al-cmt-send:disabled{opacity:.5;cursor:default}
  /* ENG-7108: dvh for the same reason as #frame - the drawer's own scroll area
     ended below the visible viewport, so its composer was unreachable on a
     phone. Height ONLY: the width is deliberately left at the fixed panel width,
     because ENG-8510 already takes it to 100vw below the split threshold via a
     media query, and canvasInsetPx assumes this base width when it reflows the
     canvas. Clamping here was redundant with that rule and broke the invariant
     its test protects - caught by that test, which is what it is for. */
  .al-drawer{position:fixed;top:0;right:0;height:100vh;height:100dvh;
    width:${AL_PANEL_WIDTH_PX}px;z-index:2147483646;
    background:#fff;box-shadow:-2px 0 16px rgba(0,0,0,.18);display:block;transition:width .18s ease}
  .al-drawer-frame{border:0;width:100%;height:100%;display:block}
  /* Vertical edge tab, centered on the panel and overlapping its left edge by a
     few px (width 24 at left:-20 ⇒ 4px sits over the panel) so it reads attached. */
  .al-drawer-collapse{position:absolute;top:50%;left:-20px;transform:translateY(-50%);
    width:24px;height:60px;border:0;border-radius:8px 0 0 8px;cursor:pointer;
    font-size:18px;line-height:1;color:#fff;background:rgba(11,16,32,.82);
    box-shadow:-2px 0 8px rgba(0,0,0,.2)}
  /* Collapsed: the panel shrinks to nothing; the launcher bubble is the entry. */
  .al-drawer.collapsed{width:0}
  .al-drawer.collapsed .al-drawer-frame{display:none}
  .al-drawer.collapsed .al-drawer-collapse{display:none}
  /* ENG-8510 small-viewport rule. Below ${AL_SPLIT_MIN_VIEWPORT_PX}px
     (${AL_PANEL_WIDTH_PX}px panel + ${AL_MIN_CANVAS_PX}px of canvas worth
     looking at) we do NOT split - a ${AL_PANEL_WIDTH_PX}px panel beside a 600px
     phone leaves a strip the scaler would render the slide into at ~17%. The
     panel takes the whole viewport instead and the canvas stays full size
     underneath (the shell script holds --al-canvas-inset at 0 here), so closing
     the panel brings the slide straight back at full scale.
     Specificity note: .al-drawer.collapsed (0,2,0) still beats this (0,1,0),
     so a collapsed panel stays 0-wide regardless of source order. */
  @media (max-width:${AL_SPLIT_MIN_VIEWPORT_PX - 1}px){ .al-drawer{width:100vw} }
  /* Full-width panel: tuck the tab to the INNER right edge so it stays on-screen.
     The tab is normally at left:-20px, overlapping the panel's left edge from
     outside. That only works while the panel has something to its left. Once the
     panel is 100vw its left edge IS the viewport edge, so left:-20px puts a
     24px-wide control 20px off-screen — 4px of it reachable, on the control that
     closes the panel.
     THIS BREAKPOINT MUST TRACK THE WIDTH RULE ABOVE (CodeRabbit, #4174). It was
     460px, a number borrowed from the launcher/label rules further down, which
     describe a different thing (when a label stops fitting). The panel goes
     full-width at ${AL_SPLIT_MIN_VIEWPORT_PX - 1}px, so 461-${AL_SPLIT_MIN_VIEWPORT_PX - 1}px
     was full-width with an off-screen close tab — every tablet-ish portrait
     width. Both rules now derive from the same constant so they cannot drift. */
  @media (max-width:${AL_SPLIT_MIN_VIEWPORT_PX - 1}px){ .al-drawer-collapse{left:auto;right:0;border-radius:8px 0 0 8px} }
  /* Persistent "Chat with <agent>" launcher (ENG-6814), bottom-right. */
  .al-launcher{position:fixed;right:16px;bottom:16px;z-index:2147483645;display:flex;align-items:center;
    gap:10px;border:0;cursor:pointer;padding:8px 20px 8px 8px;border-radius:999px;color:#fff;
    background:rgba(11,16,32,.92);box-shadow:0 4px 16px rgba(0,0,0,.3);
    font-family:system-ui,-apple-system,sans-serif;font-size:16px;font-weight:600}
  .al-launcher-av{width:40px;height:40px;border-radius:50%;object-fit:cover;flex:0 0 auto}
  .al-launcher-fb{width:40px;height:40px;border-radius:50%;flex:0 0 auto;display:flex;align-items:center;
    justify-content:center;background:rgba(255,255,255,.18);font-size:17px;font-weight:700}
  .al-launcher-label{white-space:nowrap}
  @media (max-width:460px){ .al-launcher-label{display:none} .al-launcher{padding:6px} }
  /* "View Stats" button (ENG-7098), stacked just above the chat launcher. Same
     gate as commenting; opens the stats drawer. Slightly smaller + lighter than
     the chat bubble so the chat launcher stays the primary action. */
  .al-stats-btn{position:fixed;right:16px;bottom:74px;z-index:2147483645;display:flex;align-items:center;
    gap:8px;border:0;cursor:pointer;padding:8px 16px;border-radius:999px;color:#fff;
    background:rgba(11,16,32,.82);box-shadow:0 4px 16px rgba(0,0,0,.3);
    font-family:system-ui,-apple-system,sans-serif;font-size:14px;font-weight:600}
  .al-stats-ic{width:16px;height:16px;flex:0 0 auto;display:block}
  .al-stats-label{white-space:nowrap}
  @media (max-width:460px){ .al-stats-label{display:none} .al-stats-btn{padding:8px;bottom:64px} }
  /* "Edit Mode" toggle (ENG-8477, renamed ENG-9383), stacked above View Stats. Same gate as the
     other two bubbles. It is a TOGGLE, not an action: pressed state is carried on
     aria-pressed and mirrored in the fill, because on a deck it changes what a
     click on the canvas does and the viewer has to be able to see which mode
     they are in. */
  .al-edit-btn{position:fixed;right:16px;bottom:132px;z-index:2147483645;display:flex;align-items:center;
    gap:8px;border:0;cursor:pointer;padding:8px 16px;border-radius:999px;color:#fff;
    background:rgba(11,16,32,.82);box-shadow:0 4px 16px rgba(0,0,0,.3);
    font-family:system-ui,-apple-system,sans-serif;font-size:14px;font-weight:600}
  .al-edit-btn[aria-pressed="true"]{background:#0b7a4b}
  .al-edit-ic{width:16px;height:16px;flex:0 0 auto;display:block}
  .al-edit-label{white-space:nowrap}
  @media (max-width:460px){ .al-edit-label{display:none} .al-edit-btn{padding:8px;bottom:112px} }`
    : '';

  // The comment overlay, as vanilla JS baked into the shell (the static page
  // has no React). It receives selections from the content iframe and relays
  // composed comments to the agent through the same-origin /live-bridge iframe,
  // so no credential ever lands on this artifact-serving domain. String
  // concatenation only (no template literals) to stay clear of the shell's own
  // ${} interpolation.
  const markupOverlayJs = markupEnabled
    ? `
  if (MARKUP_ENABLED) {
    const bridgeOrigin = (function(){ try { return new URL(APP_BASE).origin; } catch(e){ return ''; } })();
    let bridgeFrame = null, bridgeLoaded = false, canComment = null, pendingSel = null, box = null, sending = false;
    let launcher = null, statsBtn = null, editBtn = null, agentName = '', agentAvatar = '';
    let editModeOn = false; // ENG-8477: viewer has Edit Mode switched on
    // Same video signal the PDF-export control uses (ENG-7085/ENG-7088).
    const IS_VIDEO_ARTEFACT = ${isVideo ? 'true' : 'false'};
    const waiters = {}; let seq = 0;
    const toBridge = (msg) => { try { bridgeFrame.contentWindow.postMessage(msg, bridgeOrigin); } catch(e){} };
    // ENG-6821 click-to-edit: post a control message into the sandboxed content
    // iframe (opaque origin ⇒ '*'). The content's bridge script ignores anything
    // without the control marker. We only arm editing once the probe confirms the
    // viewer may edit; the content re-inits its listener on every (re)load, so
    // armEdit re-sends on each frame load while editing is allowed.
    let editAllowed = false;
    const toContent = (msg) => { try { frame.contentWindow.postMessage(Object.assign({ [MARKUP_CONTROL_MARKER]: true }, msg), '*'); } catch(e){} };
    // Arm BOTH inline edit (ENG-6821) and right-click element-comment (ENG-6847)
    // for an authed member. Two control messages, one flag - the content re-inits
    // its listeners on every (re)load, so we resend both on each frame load while
    // allowed (a published hot-swap reloads the iframe and would otherwise disarm).
    const armEdit = () => { editAllowed = true; toContent({ type: 'enable-edit' }); toContent({ type: 'enable-comment' }); };
    // ENG-8477: edit MODE has to be re-sent on every (re)load too, and for the
    // same reason the arming messages are - the content re-inits its state on
    // load, so a saved edit (which hot-swaps the iframe) would otherwise drop the
    // viewer silently out of edit mode after every single save.
    frame.addEventListener('load', () => {
      if (editAllowed) {
        toContent({ type: 'enable-edit' });
        toContent({ type: 'enable-comment' });
        if (editModeOn) toContent({ type: 'set-edit-mode', on: true });
      }
    });
    // Flip edit mode, keeping the button's pressed state and the content iframe
    // in step. Kept in one place so the toggle, the reload path and any future
    // caller cannot drift apart.
    const setEditMode = (on) => {
      editModeOn = !!on;
      if (editBtn) editBtn.setAttribute('aria-pressed', String(editModeOn));
      toContent({ type: 'set-edit-mode', on: editModeOn });
    };
    // Relay a saved inline edit from the content frame to the bridge, then hand the
    // result back to the content. On success the publish broadcast hot-swaps the
    // iframe to the new version (which reloads + re-arms), so nothing else to do.
    const onContentEdit = (d) => {
      if (canComment !== true) return; // only an armed (authed) viewer can edit
      // baseVersion = the version this shell is showing; the API 409s a stale edit
      // rather than dropping the optimistic-concurrency guard (current tracks the
      // latest published version, bumped by the 'published' broadcast handler).
      // textIndex (ENG-6856) is present only for a per-text-node edit inside an
      // inline-only container; pass it through so the API replaces just that run.
      call({ type: 'live-edit-text', slug: cfg.slug, path: d.path, oldText: d.oldText, newText: d.newText, textIndex: d.textIndex, baseVersion: current }, (r) => {
        toContent({ type: 'edit-result', ok: !!(r && r.ok), error: (r && r.error) || (r && r.__timeout ? 'Timed out' : '') });
      }, PROBE_TIMEOUT_MS);
    };
    // call() resolves its callback exactly once: on the matching reply, or - when
    // a timeout is given - on a synthetic { __timeout: true } so a hung bridge
    // (ENG-6836) can't strand the caller forever.
    const call = (msg, cb, timeoutMs) => {
      const n = 'm' + (++seq); let done = false;
      const fire = (d) => { if (done) return; done = true; delete waiters[n]; cb(d); };
      waiters[n] = fire; msg.nonce = n; toBridge(msg);
      if (timeoutMs) setTimeout(function(){ fire({ __timeout: true }); }, timeoutMs);
    };
    // ENG-6836: the bridge's authed probe can transiently fail or hang (the
    // cross-site iframe's Supabase session refresh). Re-probe on a timeout or a
    // retry-tagged reply, bounded, so the launcher reliably appears once the
    // session settles. A definitive canComment:false (e.g. not a member) stops.
    let probeTries = 0, probeResolved = false, probing = false;
    const PROBE_TIMEOUT_MS = 16000, PROBE_MAX_TRIES = 3, PROBE_BACKOFF_MS = 1500;
    const sendProbe = () => {
      if (!bridgeLoaded || probeResolved || probing) return;
      probing = true; probeTries++;
      call({ type: 'live-comment-probe', slug: cfg.slug }, (d) => {
        probing = false;
        if (d && d.canComment === true) {
          probeResolved = true; canComment = true;
          agentName = (d && typeof d.agentName === 'string' && d.agentName) || 'the agent';
          agentAvatar = (d && typeof d.agentAvatar === 'string' && /^https:\\/\\//i.test(d.agentAvatar)) ? d.agentAvatar : '';
          showLauncher();
          armEdit();
          if (pendingSel) { showBox(pendingSel); pendingSel = null; }
          return;
        }
        var transient = !d || d.__timeout === true || d.retry === true;
        if (transient && probeTries < PROBE_MAX_TRIES) { setTimeout(sendProbe, PROBE_BACKOFF_MS); return; }
        if (transient) {
          // Exhausted the retry budget on a TRANSIENT failure - the cross-site
          // iframe's Supabase session hadn't hydrated yet (ENG-6854). Do NOT give
          // up for good: reset to a re-probeable state (canComment back to null)
          // so a later selection/right-click, a window focus, or a bridge
          // re-announce probes again. Leaving it false stranded the overlay until
          // a full page reload (the "hit and miss" arming).
          canComment = null; probing = false; probeTries = 0;
          return;
        }
        // Definitive deny: the server resolved the member and said no. Stop here so
        // we never spam comment-access for a genuine non-member.
        canComment = false; pendingSel = null;
      }, PROBE_TIMEOUT_MS);
    };
    const ensureBridge = () => {
      if (bridgeFrame || !bridgeOrigin) return;
      bridgeFrame = document.createElement('iframe');
      bridgeFrame.setAttribute('aria-hidden', 'true');
      bridgeFrame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      bridgeFrame.src = APP_BASE + '/live-bridge';
      // ENG-6837: the iframe 'load' event fires BEFORE the bridge's React listener
      // is attached, so probing here races and is often dropped. The bridge now
      // pings 'live-bridge-ready' when it's actually listening (handled below) and
      // we probe on that. This load handler is only a fallback - probe after a
      // short delay in case that ready ping was missed (or an older bridge).
      bridgeFrame.addEventListener('load', () => { bridgeLoaded = true; setTimeout(sendProbe, 3000); tryMarkInternal(); });
      document.body.appendChild(bridgeFrame);
    };
    // ENG-7115: tag this view-session as internal (an IL-staff or owning-team
    // self-view) so the public analytics exclude it. The anonymous beacon (a
    // separate script in this same shell) hands us its session id via
    // window.__alMarkInternal once /view-session returns; we relay it to the
    // authed bridge, which re-derives internal status server-side and no-ops for
    // an external/anonymous viewer. Fires at most once, only after the bridge is
    // ready (so the session arriving first is handled on the ready ping). With no
    // bridge (appBase unset) the hook is never defined and the view counts as
    // external - safe degradation.
    let alMarkSession = null, alMarked = false, alMarking = false, alMarkTries = 0;
    var AL_MARK_MAX_TRIES = 3;
    const tryMarkInternal = () => {
      if (alMarked || alMarking || !alMarkSession || !bridgeLoaded || alMarkTries >= AL_MARK_MAX_TRIES) return;
      alMarking = true; alMarkTries++;
      // NOT gated on canComment: internal status is broader than commenting (an
      // IL staffer, or a viewer-role member, may be internal yet unable to
      // comment), and the route is the authority. alMarked latches ONLY on a
      // confirmed { ok: true } - a transient failure (bridge not ready / auth
      // hiccup / timeout) clears alMarking so a later ready or focus signal can
      // retry, bounded by AL_MARK_MAX_TRIES so an anonymous viewer can't loop.
      call({ type: 'mark-internal', slug: cfg.slug, sessionId: alMarkSession }, (r) => {
        alMarking = false;
        if (r && r.ok) alMarked = true;
      }, PROBE_TIMEOUT_MS);
    };
    window.__alMarkInternal = (sid) => { if (!sid) return; alMarkSession = sid; ensureBridge(); tryMarkInternal(); };
    // Persistent "Chat with <agent>" launcher (ENG-6814), bottom-right. Shown
    // once the probe confirms the viewer may comment; it is the collapsed state
    // and the entry point, replacing the old rail tab. Click opens the drawer.
    // Both bubbles (chat launcher + "View Stats", ENG-7098) share the same gate
    // and the same shown/hidden lifecycle - a drawer hides both; collapsing it
    // brings both back.
    const hideLauncher = () => {
      if (launcher) launcher.style.display = 'none';
      if (statsBtn) statsBtn.style.display = 'none';
      if (editBtn) editBtn.style.display = 'none';
    };
    // A small bar-chart glyph for the stats button, built as inline SVG (the
    // shell has no icon font). createElementNS so the SVG namespace is correct.
    const makeStatsIcon = () => {
      const svgNs = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNs, 'svg');
      svg.setAttribute('class', 'al-stats-ic');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('aria-hidden', 'true');
      var bars = [[1, 9, 3], [6, 5, 7], [11, 2, 10]]; // x, y, height
      for (var i = 0; i < bars.length; i++) {
        var r = document.createElementNS(svgNs, 'rect');
        r.setAttribute('x', String(bars[i][0])); r.setAttribute('y', String(bars[i][1]));
        r.setAttribute('width', '3'); r.setAttribute('height', String(bars[i][2]));
        r.setAttribute('rx', '1'); r.setAttribute('fill', 'currentColor');
        svg.appendChild(r);
      }
      return svg;
    };
    // A pencil glyph for the Edit-text toggle (ENG-8477), same inline-SVG
    // approach as the stats icon - the shell has no icon font.
    const makeEditIcon = () => {
      const svgNs = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNs, 'svg');
      svg.setAttribute('class', 'al-edit-ic');
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('aria-hidden', 'true');
      const p = document.createElementNS(svgNs, 'path');
      p.setAttribute('d', 'M11.4 1.6a1.4 1.4 0 0 1 2 2l-.8.8-2-2 .8-.8zM9.7 3.3l2 2L5 12H3v-2l6.7-6.7z');
      p.setAttribute('fill', 'currentColor');
      svg.appendChild(p);
      return svg;
    };
    const showLauncher = () => {
      if (launcher) {
        launcher.style.display = 'flex';
        if (statsBtn) statsBtn.style.display = 'flex';
        if (editBtn) editBtn.style.display = 'flex';
        return;
      }
      launcher = document.createElement('button');
      launcher.type = 'button'; launcher.className = 'al-launcher';
      launcher.setAttribute('aria-label', 'Chat with ' + agentName);
      if (agentAvatar) {
        const img = document.createElement('img'); img.className = 'al-launcher-av'; img.src = agentAvatar; img.alt = '';
        launcher.appendChild(img);
      } else {
        const fb = document.createElement('span'); fb.className = 'al-launcher-fb';
        fb.textContent = (agentName.trim().charAt(0) || '?').toUpperCase();
        launcher.appendChild(fb);
      }
      const label = document.createElement('span'); label.className = 'al-launcher-label';
      label.textContent = 'Chat with ' + agentName; launcher.appendChild(label);
      launcher.addEventListener('click', () => openDrawer());
      document.body.appendChild(launcher);
      // The "View Stats" button (ENG-7098) sits just above the chat launcher.
      statsBtn = document.createElement('button');
      statsBtn.type = 'button'; statsBtn.className = 'al-stats-btn';
      statsBtn.setAttribute('aria-label', 'View stats');
      statsBtn.appendChild(makeStatsIcon());
      const sl = document.createElement('span'); sl.className = 'al-stats-label';
      sl.textContent = 'View Stats'; statsBtn.appendChild(sl);
      statsBtn.addEventListener('click', () => openStats());
      document.body.appendChild(statsBtn);
      // ENG-8477: the Edit-text toggle, above View Stats. On a deck the click
      // zones cover the slides, so a click cannot mean both "page" and "edit" -
      // this is how the viewer says which they want. It also answers the "say so
      // in the UI" half of the ticket: editing a deck is now a visible mode
      // rather than an affordance that silently does nothing.
      // ENG-8477 + ENG-7085 precedent: not on a Remotion video. A video's text is
      // drawn by React at render time, not by DOM leaves the bridge can path to,
      // so the control could never do anything - and offering an affordance that
      // silently does nothing is the exact complaint this ticket is about. It
      // would also collide with the player's controls bar (the ENG-7691 chrome
      // override lifts the other two bubbles; there is nothing to lift here).
      if (IS_VIDEO_ARTEFACT) return;
      editBtn = document.createElement('button');
      editBtn.type = 'button'; editBtn.className = 'al-edit-btn';
      // ENG-9383: "Edit text" read as a verb, so it invited a press expecting a
      // one-shot action, and nothing about the label said the page was already
      // editable without it. The control has always BEEN a mode - it carries
      // aria-pressed and takes a green fill when on - so the label now says so.
      editBtn.setAttribute('aria-label', 'Edit Mode');
      editBtn.setAttribute('aria-pressed', 'false');
      editBtn.appendChild(makeEditIcon());
      const el2 = document.createElement('span'); el2.className = 'al-edit-label';
      el2.textContent = 'Edit Mode'; editBtn.appendChild(el2);
      editBtn.addEventListener('click', () => setEditMode(!editModeOn));
      document.body.appendChild(editBtn);
    };
    const hideBox = () => { if (box) { box.remove(); box = null; } };
    // The conversation drawer (ENG-6802, ENG-6808): a right-side panel iframe
    // pointing at the webapp /live-chat surface. Instead of fully closing, it
    // collapses to a thin rail on the right edge - the iframe stays mounted so
    // the thread + realtime survive, and the rail re-expands it.
    let drawer = null, drawerFrame = null, statsDrawer = null;
    // ENG-8510: give the canvas iframe an honest width whenever a right-hand
    // panel is open, so the content's own fit-to-window scaler refits the slide
    // instead of the panel clipping its right edge.
    //
    // Deliberately NOT a CSS transition on #frame: a transitioned width would
    // fire a resize inside the content on every animation frame, and the scaler
    // does a forced reflow per .al-item (21 of them on the deck that reported
    // this). Snapping the inset once per open/close means exactly ONE refit, so
    // there is no burst to debounce and no double-scale while the panel slides.
    const SPLIT_MIN_VW = ${AL_SPLIT_MIN_VIEWPORT_PX};
    const PANEL_W = ${AL_PANEL_WIDTH_PX};
    const syncCanvasInset = () => {
      const root = document.documentElement;
      // A collapsed chat panel occupies no width. The stats panel is removed
      // outright when closed, so its mere existence means it is open.
      const open = !!(drawer && !drawer.classList.contains('collapsed')) || !!statsDrawer;
      const vw = root.clientWidth || window.innerWidth || 0;
      // Below the split threshold the panel is full-width (see the CSS media
      // query) and the canvas keeps its full size underneath - shrinking it to a
      // sliver would be strictly worse than covering it.
      const inset = open && vw >= SPLIT_MIN_VW ? PANEL_W : 0;
      root.style.setProperty('--al-canvas-inset', inset + 'px');
    };
    // Window resizes DO arrive in bursts (a drag emits one per frame), and
    // crossing SPLIT_MIN_VW flips the policy, so coalesce to one recompute per
    // frame. rAF rather than a timeout: it lands in the same frame as the paint.
    let insetRaf = 0;
    const scheduleCanvasInset = () => {
      if (insetRaf) return;
      insetRaf = requestAnimationFrame(() => { insetRaf = 0; syncCanvasInset(); });
    };
    window.addEventListener('resize', scheduleCanvasInset);
    // Tell the open /live-chat drawer to re-seed after a new comment lands, so a
    // comment sent while the drawer is open shows without a refresh (ENG-6816).
    const refreshDrawer = () => {
      if (!drawerFrame) return;
      try { drawerFrame.contentWindow.postMessage({ type: 'live-chat-refresh' }, bridgeOrigin); } catch(e){}
    };
    // Collapsed ⟺ the launcher bubble is the visible state; expanded ⟺ the panel.
    const setCollapsed = (v) => {
      if (!drawer) return;
      drawer.classList.toggle('collapsed', v);
      var cb = drawer.querySelector('.al-drawer-collapse'); if (cb) cb.setAttribute('aria-expanded', String(!v));
      if (v) showLauncher(); else hideLauncher();
      // Collapsing/expanding changes how much width the panel claims, so the
      // canvas has to be handed the difference back (ENG-8510).
      syncCanvasInset();
    };
    const openDrawer = () => {
      // Mutual exclusivity (ENG-7098): the chat and stats drawers both occupy the
      // right edge, so opening one closes the other. Stats is stateless, so we
      // remove it outright (cheap to rebuild); chat collapses to preserve its
      // thread + realtime (see openStats).
      closeStats();
      if (drawer) { setCollapsed(false); hideLauncher(); return; }
      drawer = document.createElement('div');
      drawer.className = 'al-drawer';
      // Collapse control: collapses back to the launcher bubble.
      const collapseBtn = document.createElement('button');
      collapseBtn.type = 'button'; collapseBtn.className = 'al-drawer-collapse';
      collapseBtn.setAttribute('aria-label', 'Collapse conversation');
      collapseBtn.setAttribute('aria-expanded', 'true');
      collapseBtn.textContent = '\\u203a'; // chevron right
      collapseBtn.addEventListener('click', () => setCollapsed(true));
      const dframe = document.createElement('iframe');
      dframe.className = 'al-drawer-frame';
      dframe.title = 'Conversation';
      dframe.src = APP_BASE + '/live-chat?slug=' + encodeURIComponent(cfg.slug);
      drawerFrame = dframe;
      drawer.appendChild(collapseBtn);
      drawer.appendChild(dframe);
      document.body.appendChild(drawer);
      hideLauncher();
      // One inset change for the whole stats->chat swap: closeStats() above
      // deliberately does not sync, so the canvas never bounces out to full
      // width and straight back in between the two panels (ENG-8510).
      syncCanvasInset();
    };
    // The stats drawer (ENG-7098): a right-side panel iframe pointing at the
    // webapp /live-stats surface, which fetches this artifact's view/dwell
    // analytics as the authed viewer. Unlike chat it holds no live thread, so
    // closing it just removes the iframe (the launcher re-appears).
    // Does NOT sync the canvas inset: every caller either opens another panel
    // straight after (openDrawer) or syncs itself (the collapse button). Syncing
    // here would make the canvas jump to full width mid-swap (ENG-8510).
    const closeStats = () => { if (statsDrawer) { statsDrawer.remove(); statsDrawer = null; } };
    const openStats = () => {
      if (statsDrawer) return;
      statsDrawer = document.createElement('div');
      statsDrawer.className = 'al-drawer';
      // Collapse the chat drawer (preserve its thread) so only one panel shows.
      // statsDrawer is assigned FIRST on purpose: setCollapsed syncs the canvas
      // inset, and if it saw "nothing open" the canvas would scale up to full
      // width and immediately back down as stats mounts (ENG-8510).
      if (drawer) setCollapsed(true);
      const collapseBtn = document.createElement('button');
      collapseBtn.type = 'button'; collapseBtn.className = 'al-drawer-collapse';
      // One-way close (unlike the chat rail's toggle), so no aria-expanded state.
      collapseBtn.setAttribute('aria-label', 'Close stats');
      collapseBtn.textContent = '\\u203a'; // chevron right
      collapseBtn.addEventListener('click', () => { closeStats(); showLauncher(); syncCanvasInset(); });
      const sframe = document.createElement('iframe');
      sframe.className = 'al-drawer-frame';
      sframe.title = 'Stats';
      sframe.src = APP_BASE + '/live-stats?slug=' + encodeURIComponent(cfg.slug);
      statsDrawer.appendChild(collapseBtn);
      statsDrawer.appendChild(sframe);
      document.body.appendChild(statsDrawer);
      hideLauncher();
    };
    function showBox(sel) {
      hideBox();
      const el = document.createElement('div');
      el.className = 'al-cmt';
      // ENG-7108: the placement limit must use the SAME clamped width the CSS
      // rule applies, not the unclamped 288. Raised in review, and it was a bug
      // I introduced in this PR: clamping the width in CSS while leaving the JS
      // computing from 288 means that on a narrow screen the two disagree, and
      // the popover is positioned for an element wider than it actually is -
      // pushing it back off the edge the clamp exists to keep it on.
      const cmtW = Math.min(288, window.innerWidth - 24);
      el.style.left = Math.max(8, Math.min(sel.rect.left, window.innerWidth - cmtW - 8)) + 'px';
      el.style.top = (sel.rect.bottom + 8) + 'px';
      const q = document.createElement('p'); q.className = 'al-cmt-q'; q.textContent = sel.text; el.appendChild(q);
      const ta = document.createElement('textarea'); ta.className = 'al-cmt-ta'; ta.rows = 2;
      ta.placeholder = 'Comment for the agent…'; el.appendChild(ta);
      const err = document.createElement('p'); err.className = 'al-cmt-err'; err.style.display = 'none'; el.appendChild(err);
      const row = document.createElement('div'); row.className = 'al-cmt-row';
      const cancel = document.createElement('button'); cancel.type = 'button'; cancel.className = 'al-cmt-cancel';
      cancel.textContent = 'Cancel'; cancel.addEventListener('click', hideBox);
      const send = document.createElement('button'); send.type = 'button'; send.className = 'al-cmt-send'; send.textContent = 'Send';
      const doSend = () => {
        const c = ta.value.trim(); if (!c || sending) return;
        sending = true; err.style.display = 'none'; send.disabled = true; send.textContent = 'Sending…';
        call({ type: 'live-comment-send', slug: cfg.slug, selectedText: sel.text, comment: c, target: sel.target || '', kind: sel.kind || 'text' }, (d) => {
          sending = false; send.disabled = false; send.textContent = 'Send';
          if (d && d.ok) {
            // The comment landed in the stable session; open the conversation
            // drawer (ENG-6802) so it shows in-thread and the reply streams in.
            hideBox();
            const wasOpen = !!drawer;
            openDrawer();
            // If the drawer was already open, realtime won't echo the new comment
            // into it (ENG-6816) - tell it to re-seed. A just-opened drawer loads
            // history on mount, so the refresh is only needed when it was open.
            if (wasOpen) refreshDrawer();
          } else {
            // __timeout (ENG-6836: bridge unreachable) or an explicit error - the
            // box stays open so the user can simply retry.
            err.textContent = (d && d.error) ? d.error : (d && d.__timeout) ? 'Timed out - please try again' : 'Failed to send';
            err.style.display = 'block';
          }
        }, PROBE_TIMEOUT_MS);
      };
      send.addEventListener('click', doSend);
      ta.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); doSend(); } });
      row.appendChild(cancel); row.appendChild(send); el.appendChild(row);
      document.body.appendChild(el); box = el; ta.focus();
    }
    // rect comes from the untrusted (sandboxed) content iframe; reject a malformed
    // one so showBox's rect.left/rect.bottom reads can't throw and wedge the handler.
    const validRect = (r) => !!r && typeof r.left === 'number' && typeof r.bottom === 'number';
    const onSelection = (text, rect, target) => {
      if (!text || !validRect(rect)) return;
      ensureBridge();
      if (canComment === true) showBox({ text: text, rect: rect, target: target });
      else if (canComment === null) { pendingSel = { text: text, rect: rect, target: target }; if (bridgeLoaded) sendProbe(); }
    };
    // ENG-6847: a right-clicked element. Same prompt + send path as a selection,
    // but flagged kind:'element' so the bridge quotes the element reference (not a
    // text selection) when composing the agent message. The content only emits
    // 'element' once armEdit has armed commenting (canComment===true by then).
    const onElement = (label, rect, target) => {
      if (!label || !validRect(rect)) return;
      ensureBridge();
      if (canComment === true) showBox({ text: label, rect: rect, target: target, kind: 'element' });
      else if (canComment === null) { pendingSel = { text: label, rect: rect, target: target, kind: 'element' }; if (bridgeLoaded) sendProbe(); }
    };
    window.addEventListener('message', (e) => {
      // Selections from the sandboxed content iframe (opaque origin, so match
      // by window identity, never origin).
      if (e.source === frame.contentWindow) {
        const d = e.data;
        if (!d || d[MARKUP_MARKER] !== true) return;
        if (d.type === 'clear') { if (box && !sending) hideBox(); return; }
        if (d.type === 'selection') onSelection(d.text, d.rect, d.target);
        if (d.type === 'element') onElement(d.label, d.rect, d.target);
        if (d.type === 'edit') onContentEdit(d);
        return;
      }
      // Relay replies from the bridge (validate BOTH the window and the origin).
      if (bridgeFrame && e.source === bridgeFrame.contentWindow && e.origin === bridgeOrigin) {
        const m = e.data;
        // ENG-6837: the bridge announces when its listener is live - probe now,
        // instead of racing the iframe 'load' event (which dropped the probe and
        // left the launcher hidden). sendProbe is guarded against double-probing.
        if (m && m.type === 'live-bridge-ready') { bridgeLoaded = true; sendProbe(); tryMarkInternal(); return; }
        if (!m || !m.nonce || !waiters[m.nonce]) return;
        const cb = waiters[m.nonce]; delete waiters[m.nonce]; cb(m);
      }
    });
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape') hideBox(); });
    // ENG-6854: self-heal the arming. If the first probe gave up transiently
    // (canComment reset to null above), re-probe when the tab regains focus or
    // becomes visible - so the launcher / edit / right-click recover on their own
    // without a full reload. Guards keep it to a single in-flight probe and never
    // re-probe once armed (true) or definitively denied (false).
    const reprobeIfIdle = () => { tryMarkInternal(); if (canComment === null && bridgeLoaded && !probing && !probeResolved) sendProbe(); };
    window.addEventListener('focus', reprobeIfIdle);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) reprobeIfIdle(); });
    // ENG-6814: probe on load so the "Chat with <agent>" launcher appears for an
    // authenticated member immediately, not only after a text selection.
    ensureBridge();
  }`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="agt-shell-version" content="${SHELL_VERSION}">
<title>${htmlEscape(titleText)}</title>
${ogHtml}
<style>
  html,body{margin:0;height:100%;background:#fff}
  /* ENG-8510: the canvas is the right-hand panel's SPLITTER PARTNER, not
     something the panel paints over. The fit-to-window scaler baked into
     .al-item content measures the IFRAME's own viewport, so the only honest way
     to stop a panel clipping a slide is to actually take the width away from the
     iframe - it then fires a real resize inside itself and the scaler refits.
     Nothing about this parent-document panel leaks into the content.
     --al-canvas-inset is set by the shell script; the 0px fallback means a
     shell whose script never runs behaves exactly as it did before. */
  /* ENG-7108: 100dvh, with 100vh first as the fallback for browsers without
     dynamic viewport units. On mobile, vh resolves to the LARGE viewport - the
     height the page would have if the address bar were hidden - so the iframe
     ran taller than the visible area and the bottom of every artefact sat behind
     browser chrome. On a phone that is the last line of a doc or the bottom of a
     slide, i.e. exactly the part a recipient scrolls to. Declaration order is
     load-bearing: a browser that does not understand dvh keeps vh. */
  #frame{border:0;width:calc(100% - var(--al-canvas-inset,0px));height:100vh;height:100dvh;display:block}
  /* "Updating…" overlay — a 50% scrim shown while a newly-published version
     loads in, cleared once the new content has rendered. */
  #al-updating{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;
    background:rgba(11,16,32,.5);opacity:0;visibility:hidden;transition:opacity .25s ease;
    pointer-events:none;font-family:system-ui,-apple-system,sans-serif;z-index:2147483647}
  #al-updating.show{opacity:1;visibility:visible}
  #al-updating span{background:rgba(0,0,0,.72);color:#fff;padding:.65rem 1.25rem;border-radius:999px;
    font-size:18px;font-weight:500;display:flex;align-items:center;gap:.6rem}
  #al-updating span::before{content:"";width:10px;height:10px;border-radius:50%;background:#6ee7b7;
    animation:al-pulse 1s ease-in-out infinite}
  /* When the agent has an avatar it stands in for the pulsing dot. */
  #al-updating span.has-avatar::before{display:none}
  #al-updating .al-av{width:32px;height:32px;border-radius:50%;object-fit:cover;flex:0 0 auto;
    background:rgba(255,255,255,.18);animation:al-pulse 1.4s ease-in-out infinite}
  @keyframes al-pulse{0%,100%{opacity:1}50%{opacity:.25}}
  /* Download button — fixed bottom-left overlay over the iframe, faded until
     hover so it doesn't sit on the artifact. Same overlay layer as #al-updating.
     Raised off the bottom edge so it clears a player's playback bar if a video
     ever slips the isVideo gate (ENG-7085 — videos normally omit the button). */
  #al-download{position:fixed;left:16px;bottom:${DOWNLOAD_BOTTOM_PX}px;z-index:2147483646;
    display:inline-flex;align-items:center;gap:.4rem;text-decoration:none;
    font-family:system-ui,-apple-system,sans-serif;font-size:13px;font-weight:600;
    color:#fff;background:rgba(11,16,32,.82);padding:.5rem .85rem;border-radius:999px;
    box-shadow:0 2px 10px rgba(0,0,0,.25);opacity:.55;transition:opacity .2s ease}
  #al-download:hover,#al-download:focus{opacity:1}
  /* CS-1763: on a phone the pill is the ONLY route to a readable deck, so it
     stops being a faded affordance that reveals itself on hover - there is no
     hover on touch, which is why .55 opacity was effectively permanent there.
     Also given a 44px minimum height, the standard touch target.

     The bottom offset IS DELIBERATELY NOT CHANGED HERE. Obi Wan 88480452d155: my first
     version set bottom:16px, which drops the pill into the 20->68px band that
     DECK_HUD_BOTTOM_PX / DECK_HUD_TOP_PX reserve for the deck prev/next HUD,
     and across the base attribution badge at 14->~40px. With z-index
     2147483646 the pill paints over both, so on a 390px phone it lands on the
     deck's PREV BUTTON - reintroducing the ENG-8668 collision on the very
     surface this PR exists to fix, and occluding the attribution link ENG-7469
     documents as non-occluding.

     72px was chosen to clear exactly that. Growing the pill to a 44px target
     extends it UPWARDS from 72 (72->116px), which stays clear; moving its
     anchor down does not. The touch target was the goal, the reposition was
     me not asking what the 72 was for. */
  @media (max-width:640px){
    #al-download{opacity:1;min-height:${DOWNLOAD_MOBILE_MIN_HEIGHT_PX}px;padding:.65rem 1rem;font-size:14px}
  }
  /* The "Open" action, styled as the quieter sibling of #al-dl-go so the two
     read as a pair rather than as one button and one link. */
  /* 44px minimum touch target. CodeRabbit on PR 6408: I gave the pill one and
     not the link I added in the same change - on the PR whose entire subject is
     making this usable on a phone. Padding plus line-height can land under 44px
     on its own, so the floor is explicit.
     (No backticks in this block: the whole stylesheet is a template literal, so
     one would terminate the string. That is how the first attempt broke tsc.) */
  #al-dl-open{display:inline-flex;align-items:center;box-sizing:border-box;min-height:44px;
    text-decoration:none;cursor:pointer;background:transparent;color:#0b1020;
    font-size:14px;font-weight:600;padding:.55rem .8rem;border-radius:8px;
    border:1px solid #cdd3e0}
  #al-dl-open:hover,#al-dl-open:focus{background:#f1f3f8}
  #al-download::before{content:"";width:14px;height:14px;flex:0 0 auto;
    background:no-repeat center/contain url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'/%3E%3Cpolyline points='7 10 12 15 17 10'/%3E%3Cline x1='12' y1='15' x2='12' y2='3'/%3E%3C/svg%3E")}
  /* ENG-7054/ENG-7097: download-confirmation modal. The pill (#al-download) is
     now a button that opens this dialog; the real export link is #al-dl-go. */
  #al-download{border:0;cursor:pointer}
  #al-dl-modal[hidden]{display:none}
  #al-dl-modal{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;
    justify-content:center;background:rgba(11,16,32,.55);padding:16px;
    font-family:system-ui,-apple-system,sans-serif}
  #al-dl-panel{background:#fff;color:#0b1020;max-width:380px;width:100%;border-radius:14px;
    padding:22px 22px 18px;box-shadow:0 12px 40px rgba(0,0,0,.3)}
  #al-dl-title{margin:0 0 6px;font-size:18px;font-weight:700}
  #al-dl-desc{margin:0 0 18px;font-size:14px;line-height:1.45;color:#475069}
  #al-dl-actions{display:flex;justify-content:flex-end;gap:10px;align-items:center}
  #al-dl-cancel{border:0;cursor:pointer;background:transparent;color:#475069;font-size:14px;
    font-weight:600;padding:.55rem .8rem;border-radius:8px}
  #al-dl-cancel:hover{background:#eef1f6}
  #al-dl-go{text-decoration:none;cursor:pointer;background:#0b1020;color:#fff;font-size:14px;
    font-weight:700;padding:.55rem 1rem;border-radius:8px}
  #al-dl-go:hover{background:#1b2236}
  /* ENG-10468: the busy state held while the export renders. pointer-events is
     belt to the JS guard's braces - a keyboard Enter still fires a click, so the
     handler's own busy check is what actually stops a second export. */
  #al-dl-go[aria-busy="true"]{background:#3a4460;pointer-events:none}
  /* ENG-7469: free-tier attribution footer. Restrained bottom-center pill, faded
     until hover, on the same overlay layer as the other chrome (below the
     download modal/button). Non-occluding: small, translucent, pointer-through
     until hovered is NOT set (the link must stay clickable), but it sits clear of
     the artefact's centre. */
  #al-attribution{position:fixed;left:50%;bottom:14px;transform:translateX(-50%);
    z-index:2147483645;display:inline-flex;align-items:center;text-decoration:none;
    font-family:system-ui,-apple-system,sans-serif;font-size:12px;font-weight:600;
    color:#fff;background:rgba(11,16,32,.72);padding:.35rem .7rem;border-radius:999px;
    box-shadow:0 2px 8px rgba(0,0,0,.2);opacity:.6;transition:opacity .2s ease}
  #al-attribution:hover,#al-attribution:focus{opacity:1}
  /* ENG-7470: sunset countdown banner (free tier, grace window). A slim top bar;
     stays clear of the artefact so the page remains usable during the window. */
  #al-sunset{position:fixed;top:0;left:0;right:0;z-index:2147483644;
    font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.3;
    color:#0b1020;background:#ffe6a6;border-bottom:1px solid #e6c976;
    padding:.5rem 1rem;text-align:center;box-shadow:0 1px 6px rgba(0,0,0,.12)}
  #al-sunset a{color:#0b1020;font-weight:700;text-decoration:underline}${presenterChromeCss}${markupStyles}${videoChromeOverrideCss}${deckChromeCss}
</style>
</head>
<body>
<iframe id="frame"${iframeSrcAttr}
  sandbox="allow-scripts allow-forms allow-popups allow-modals"
  title="${htmlEscape(titleText)}"></iframe>
<div id="al-updating" aria-hidden="true"><span>Updating&hellip;</span></div>
<div id="al-chrome-hint" role="status" aria-live="polite"><kbd>H</kbd> hides these controls</div>
${downloadHtml}
${footerHtml}
${sunsetHtml}
<script type="module">
  const cfg = ${cfg};
  let current = cfg.version;
  const frame = document.getElementById('frame');
  const overlay = document.getElementById('al-updating');
  // ENG-6788: when the comment overlay is on, load content via srcDoc with the
  // selection bridge injected; otherwise the iframe uses its static src.
  const MARKUP_ENABLED = ${markupEnabled ? 'true' : 'false'};
  const MARKUP_BRIDGE = ${bridgeLiteral};
  const MARKUP_MARKER = ${markerLiteral};
  const MARKUP_CONTROL_MARKER = ${controlMarkerLiteral};
  const APP_BASE = ${appBaseLiteral};
  // ENG-7057: scroll/section reporter injected into the sandboxed content so the
  // shell beacon can receive scroll depth via postMessage. Empty string when
  // analytics is unconfigured.
  const VIEW_REPORTER = ${viewReporterLiteral};
  // ENG-7692: portrait Remotion video pillarbox fix injected via srcDoc.
  // VIDEO_PORTRAIT_FIX is a self-invoking script element (non-empty only for
  // isVideo artefacts) that detects and corrects a mismatched Player container
  // aspectRatio at runtime, so existing published 9:16 artefacts are fixed
  // without republishing. Empty string for all non-video artefacts.
  const IS_VIDEO = ${isVideo ? 'true' : 'false'};
  const VIDEO_PORTRAIT_FIX = ${videoPortraitFixLiteral};
  const NEEDS_SRCDOC = ${needsSrcdoc ? 'true' : 'false'};
  const loadVersion = (v) => {
    const url = cfg.base + '/content/' + v + '.html';
    if (!NEEDS_SRCDOC) { frame.src = url; return; }
    // Same-origin fetch of the immutable content, injected (markup bridge and/or
    // the analytics reporter), handed to the sandboxed (opaque-origin) iframe via
    // srcdoc. Degrade to a plain src load if the fetch fails.
    fetch(url, { credentials: 'omit' })
      .then((r) => r.text())
      .then((html) => { frame.srcdoc = html + (MARKUP_ENABLED ? MARKUP_BRIDGE : '') + VIEW_REPORTER + (IS_VIDEO ? VIDEO_PORTRAIT_FIX : ''); })
      .catch(() => { frame.removeAttribute('srcdoc'); frame.src = url; });
  };
  if (NEEDS_SRCDOC) loadVersion(cfg.version);
  // ENG-7054/ENG-7097: download-confirmation modal. Clicking the Download pill
  // opens the dialog; the file only fetches when the user clicks "Download PDF"
  // (#al-dl-go, a real cross-origin <a download> to the export route). Backdrop
  // click, Cancel, and Escape dismiss it. No-op when the button isn't rendered
  // (exportBase unset).
  //
  // ENG-10468: the body lives in downloadDialogScript() so a test can execute
  // the real shipped string. Read its header before changing anything here -
  // the first click must not be preventDefault'd.
${downloadDialogScript()}
  let hideTimer;
  const hide = () => { overlay.classList.remove('show'); clearTimeout(hideTimer); };
  // Build the overlay pill (publishing agent's name + optional avatar) and show
  // it, auto-clearing after safetyMs. Shared by the 'editing' signal (agent has
  // STARTED working, before the new version exists) and the 'published' ping
  // (new version landed). textContent + createElement only — a display name or
  // avatar URL can never inject markup.
  const showOverlay = (payload, safetyMs) => {
    const who = payload && typeof payload.agentName === 'string' && payload.agentName.trim();
    const avatar =
      payload && typeof payload.agentAvatar === 'string' && /^https:\\/\\//i.test(payload.agentAvatar)
        ? payload.agentAvatar
        : '';
    const span = overlay.querySelector('span');
    span.textContent = '';
    span.classList.remove('has-avatar');
    if (avatar) {
      const img = document.createElement('img');
      img.className = 'al-av';
      img.src = avatar;
      img.alt = '';
      span.appendChild(img);
      span.classList.add('has-avatar');
    }
    span.appendChild(
      document.createTextNode(who ? who + ' is updating this page… Please wait' : 'Updating…'),
    );
    overlay.classList.add('show');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, safetyMs);
  };
  // ENG-6838: durable editing state. The 'editing' broadcast below is
  // fire-and-forget, so a viewer who opens a link-first page WHILE the agent is
  // still working (e.g. generating an image) never receives it. Read the
  // persisted state and show the overlay if work is ongoing; the 'published' ping
  // clears it. NOT awaited - a slow/stalled fetch must not delay realtime init.
  //
  // ENG-8508: honour the signal's AGE. The old rule was "editing===true -> show
  // for 180s", and because that timer is per PAGE LOAD, an editing:true left
  // behind by an interrupted publish gave EVERY later visitor a fresh three
  // minutes, forever. Now the state carries editingAt and a viewer shows only
  // the time actually remaining.
  //
  // A missing editingAt is treated as STALE, not as fresh. That is the whole
  // point: state written before this shipped, or by an interrupted older path,
  // has no timestamp and is exactly the stuck case. Worst case for a genuinely
  // in-flight edit is a missing overlay for one compose window; the alternative
  // is a permanent one on a page the author is about to send to a client. The
  // realtime 'editing' broadcast still covers anyone with the page already open.
  fetch(cfg.base + '/state.json', { cache: 'no-store' })
    .then((sr) => (sr.ok ? sr.json() : null))
    .then((st) => {
      if (!st || st.editing !== true) return;
      const at = typeof st.editingAt === 'number' ? st.editingAt : 0;
      // Clamped to the 180s ceiling, not just floored at 0: a viewer whose clock
      // runs behind the writer's (or an editingAt stamped in the future) would
      // otherwise compute MORE than the full window and hold the overlay longer
      // than the expiry this ticket is meant to guarantee.
      const remaining = at ? Math.min(180000, 180000 - (Date.now() - at)) : 0;
      if (remaining > 0) showOverlay(st, remaining);
    })
    .catch(() => { /* no state yet (page never edited) - ignore */ });
  try {
    const { createClient } = await import('https://esm.sh/@supabase/supabase-js@2');
    const sb = createClient(cfg.supabaseUrl, cfg.anonKey, { auth: { persistSession: false } });
    sb.channel(cfg.channel, { config: { broadcast: { self: false } } })
      // The agent signalled it is about to update this artifact — hold the
      // overlay through the (potentially long) compose window. A generous
      // safety clear covers the case where the publish never arrives.
      .on('broadcast', { event: 'editing' }, ({ payload }) => {
        showOverlay(payload, 180000);
      })
      // The new version is live: dim, swap the iframe, clear on load (with a
      // shorter fallback in case the iframe load event never fires).
      .on('broadcast', { event: 'published' }, ({ payload }) => {
        const v = payload && payload.version;
        if (typeof v === 'number' && v > current) {
          current = v;
          showOverlay(payload, 6000);
          frame.addEventListener('load', hide, { once: true });
          loadVersion(v);
        }
      })
      .subscribe();
  } catch (err) {
    // Realtime is an enhancement; a cold reload still gets the latest version.
    console.warn('Augmented Live: realtime unavailable', err);
  }
  // ENG-7057: anonymous view-analytics beacon (inert when cfg.api is null).
  ${viewBeaconJs}
${markupOverlayJs}
${presenterModeScript()}
</script>
</body>
</html>
`;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function stripScheme(domain: string): string {
  return domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

function htmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
